import {
  buildHttpResponse,
  buildMultipartHttpResponse,
  HttpMethod,
} from "../../../Rest_api/Network_utility";
import { ApiResponse } from "../../../models/api_res";
import { getMonthFromDate, getYearFromDate } from "../../../extensions/date";
import {
  convertToRajasthanGenData,
  DailyDiscomDrawalSchedule,
  HarayanaDailyPeakData,
  RajashtanRrvpn,
  RajasthanCollective,
  RajasthanDiscomData,
  RajasthanDiscomDrawal,
  RajasthanGenData,
  RajasthanGnaTgna,
  RajasthanIsgs,
  RajasthanUploadFile,
  RajBankingData,
  RajGenParams,
  RajGenProfile,
  RajProcurement,
  RajProcurementRecord,
  RajSurplusDeficitData,
  RajTamData,
  ReEnergyData,
  GssLoadDailyDiscomResponse,
  GssLoadDailyDiscomItem,
  ChhattisgarhDiscomData,
  ChhattisgarhDCNetScheduleItem,
  ChhattisgarhForecastData,
} from "../../../models/rajasthan";
import { AnyCnameRecord } from "node:dns";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://gnai-agent.gna.energy";

function toArray<T>(res: any): T[] {
  if (Array.isArray(res)) return res;
  if (res && res.data && Array.isArray(res.data)) return res.data;
  return [];
}

export async function getRajasthanDiscomDrawlData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanDiscomDrawal?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const response = toArray<any[]>(res);
    if (!response || response.length === 0) return [];

    // Columns: [date, slot, station, key_name, district, divisional_headquarter, discom, value_mw]
    //           [0]   [1]   [2]      [3]        [4]       [5]                     [6]     [7]
    const uniqueDates = Array.from(new Set(response.map((item) => item[0])));

    return response.map((item) => {
      const mw = parseFloat(item[7]) || 0;
      return {
        dateIndex: (uniqueDates.indexOf(item[0]) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        date: item[0], // date
        month: getMonthFromDate(item[0]),
        block: item[1], // slot
        area: item[2], // station (GSS name)
        key_name: item[3], // key_name
        district: item[4], // district
        // area: item[5], // divisional_headquarter → Group By Area
        discom: item[6], // discom (JDVVNL / JVVNL / AVVNL)
        mw,
        mwh: mw / 4,
        lu: mw / 400,
      };
    });
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}

export async function dmrcChatGptRes({
  query,
  thread_token,
}: {
  query: string;
  thread_token: string;
}): Promise<{ sender: String; content: String }> {
  const response = await buildHttpResponse({
    endPoint: `dmrc/handeUserQuery?query=${query}&thread_token=${thread_token}`,
    method: HttpMethod.GET,
  });
  return response;
}

export async function createThread(): Promise<{ thread_token: string }> {
  const response = await buildHttpResponse({
    endPoint: `dmrc/create_thread`,
    method: HttpMethod.POST,
  });
  return response;
}

export async function getRajasthanDiscomDrawlForecastData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanDiscomDrawalForecast?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const response = toArray<any[]>(res);
    if (!response || response.length === 0) return [];

    // Columns: [?, date, block, station, value_mw, discom, divisional_headquarter, district, ...]
    //           [0] [1]  [2]   [3]      [4]        [5]     [6]                     [7]
    const uniqueDates = Array.from(new Set(response.map((item) => item[1])));

    return response.map((item) => {
      const mw = parseFloat(item[4]) || 0;
      return {
        dateIndex: (uniqueDates.indexOf(item[1]) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        date: item[1], // date
        month: getMonthFromDate(item[1]),
        block: item[2], // slot / block
        station: item[3], // GSS station name
        area: item[6], // divisional_headquarter → Group By Area
        discom: item[5], // discom (JDVVNL / JVVNL / AVVNL)
        district: item[7], // district
        mw,
        mwh: mw / 4,
        lu: mw / 400,
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Rajasthan Discom Drawl Forecast data:",
      error,
    );
    return [];
  }
}

export async function getRajasthanAreaForecastData({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}): Promise<RajasthanDiscomDrawal[]> {
  try {
    const response: any[] = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanAreaForecastData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    return response.map((item) => {
      return {
        date: item[1],
        month: getMonthFromDate(item[1]),
        block: item[2],
        area: item[3],
        mw: item[4],
        discom: item[5],
        division: item[6],
        district: item[7],
        mwh: 0,
      };
    });
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}

export async function getRajasthanDiscomData(
  startDate: string,
  endDate: string,
): Promise<RajasthanDiscomData[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanDiscomData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    // ✅ API returns { success, count, data: [...] }
    const data: RajasthanDiscomData[] = Array.isArray(res.data) ? res.data : [];
    if (data.length === 0) return [];

    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));

    return data.map((item) => ({
      ...item,
      date: item.date,
      block: item.block,
      discom: item.discom,

      drawal_mw: item.drawal_mw,
      drawal_mwh: item.drawal_mw / 4,
      drawal_lu: item.drawal_mw / 400,

      schedule_mw: item.schedule_mw,
      schedule_mwh: item.schedule_mw / 4,
      schedule_lu: item.schedule_mw / 400,

      load_shedding_mw: item.load_shedding_mw, // ✅ present in response
      load_shedding_mwh: item.load_shedding_mw / 4,
      load_shedding_lu: item.load_shedding_mw / 400,

      total_mw: item.drawal_mw + item.load_shedding_mw,
      total_mwh: (item.drawal_mw + item.load_shedding_mw) / 4,
      total_lu: (item.drawal_mw + item.load_shedding_mw) / 400, // ✅ was missing

      dateIndex: `${uniqueDates.indexOf(item.date) + 1} Day`,
      dates: `${startDate} to ${endDate}`,
      month: getMonthFromDate(item.date),
    }));
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}
export async function getChhattisgarhDiscomData(
  startDate: string,
  endDate: string,
): Promise<ChhattisgarhDiscomData[]> {
  try {
    const response: Promise<any[]> = await buildHttpResponse({
      endPoint: `chhattisgarh/getDemand?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res;

    const uniqueDates = Array.from(new Set(data.map((item: any) => item.date)));
    return data.map((item: any) => {
      return {
        ...item,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        block: item.slot,
        demand_mw: parseFloat(item.demand_mw.toString()),
        demand_mwh: parseFloat(item.demand_mw.toString()) / 4,
        demand_lu: parseFloat(item.demand_mw.toString()) / 4000,
        month: getMonthFromDate(item.date),
        year: getYearFromDate(item.date),
      };
    });
  } catch (error) {
    console.error("Error fetching chhattisgarh Discom data:", error);
    return [];
  }
}
export async function getChhattisgarhDCNetScheduleData(
  startDate: string,
  endDate: string,
): Promise<ChhattisgarhDCNetScheduleItem[]> {
  try {
    const response: any = await buildHttpResponse({
      endPoint: `chhattisgarh/dc-vs-actual/?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = Array.isArray(res) ? res : res.data || [];
    return data.map((item: any) => {
      return {
        ...item,
        block: item.slot,
        dc_mw: parseFloat(item.dc_mw?.toString() ?? "0"),
        actual_mw: parseFloat(item.actual_mw?.toString() ?? "0"),
        diff_mw: parseFloat(item.diff_mw?.toString() ?? "0"),
        month: getMonthFromDate(item.date),
        dates: `${startDate} to ${endDate}`,
      };
    });
  } catch (error) {
    console.error("Error fetching chhattisgarh DC Net Schedule data:", error);
    return [];
  }
}

export async function getChhattisgarhDCvsNetSchedMetrics({
  startDate,
  endDate,
  block,
  trader,
  seller_actual,
  seller_dc,
}: {
  startDate: string;
  endDate: string;
  block?: string;
  trader?: string;
  seller_actual?: string;
  seller_dc?: string;
}): Promise<any> {
  try {
    let query = `chhattisgarh/getChhattisgarhDCvsNetSchedMetrics?start_date=${startDate}&end_date=${endDate}`;
    if (block) query += `&block=${block}`;
    if (trader) query += `&trader=${trader}`;
    if (seller_actual) query += `&seller_actual=${seller_actual}`;
    if (seller_dc) query += `&seller_dc=${seller_dc}`;

    const res = await buildHttpResponse({
      endPoint: query,
      method: HttpMethod.GET,
    });
    return res;
  } catch (error) {
    console.error(
      "Error fetching chhattisgarh DC vs Net Schedule metrics:",
      error,
    );
    return null;
  }
}

export async function getChhattisgarhForecastData(
  startDate: string,
  endDate: string,
): Promise<ChhattisgarhForecastData[]> {
  try {
    const response: Promise<any[]> = await buildHttpResponse({
      endPoint: `chhattisgarh/getForecastData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res;

    const uniqueDates = Array.from(new Set(data.map((item: any) => item.date)));
    return data.map((item: any) => {
      const forecastMw =
        item.demand_forecast_mw != null
          ? parseFloat(item.demand_forecast_mw.toString())
          : 0;
      return {
        ...item,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        block: item.slot,
        forecast_type: item.forecast_type || "T+1",
        demand_forecast_mw: forecastMw,
        demand_forecast_mwh: forecastMw / 4,
        demand_forecast_lu: forecastMw / 4 / 100,
        month: getMonthFromDate(item.date),
        year: getYearFromDate(item.date),
      };
    });
  } catch (error) {
    console.error("Error fetching chhattisgarh Forecast data:", error);
    return [];
  }
}

export async function getDemoRajasthanDiscomData(
  startDate: string,
  endDate: string,
): Promise<RajasthanDiscomData[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanDiscomData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data: RajasthanDiscomData[] = Array.isArray(res.data) ? res.data : [];
    if (data.length === 0) return [];

    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));

    // Map discom codes to demo names
    const discomMap: Record<string, string> = {
      JDVVNL: "Discom 1",
      JVVNL: "Discom 2",
      AVVNL: "Discom 3",
    };

    return data.map((item) => {
      return {
        ...item,
        discom: discomMap[item.discom] || item.discom, // Apply the mapping
        drawal_mwh: item.drawal_mw / 4,
        drawal_mw: item.drawal_mw,
        drawal_lu: item.drawal_mw / 400,
        total_mwh: (item.load_shedding_mw + item.drawal_mw) / 4,
        total_mw: item.load_shedding_mw + item.drawal_mw,
        schedule_mw: item.schedule_mw,
        schedule_mwh: item.schedule_mw / 4,
        schedule_lu: item.schedule_mw / 400,
        load_shedding_mwh: item.load_shedding_mw / 4,
        load_shedding_lu: item.load_shedding_mw / 400,
        dateIndex:
          (
            uniqueDates.indexOf((item as { date: string }).date) + 1
          ).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
      };
    });
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Drawl data:", error);
    return [];
  }
}
export async function getRajasthanForecastDiscomData(
  startDate: string,
  endDate: string,
  model: string,
): Promise<RajasthanDiscomData[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getDiscomForecast?start_date=${startDate}&end_date=${endDate}&model=${model}`,
      method: HttpMethod.GET,
    });

    // ✅ API returns { success, count, data: [...] }
    const data: RajasthanDiscomData[] = Array.isArray(res.data) ? res.data : [];
    if (data.length === 0) return [];

    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));

    return data.map((item) => ({
      ...item,
      date: item.date,
      block: item.block,
      discom: item.discom,
      model: item.model,
      forcast_type: item.forcast_type, // ✅ now captured from response

      drawal_mw: item.drawal_mw,
      drawal_mwh: item.drawal_mw / 4,
      drawal_lu: item.drawal_mw / 400,

      total_mw: item.drawal_mw,
      total_mwh: item.drawal_mw / 4,
      total_lu: item.drawal_mw / 400,

      schedule_mw: item.schedule_mw,
      schedule_mwh: item.schedule_mw / 4,
      schedule_lu: item.schedule_mw / 400,

      load_shedding_mwh: 0,
      load_shedding_lu: 0,

      dateIndex: `${uniqueDates.indexOf(item.date) + 1} Day`,
      dates: `${startDate} to ${endDate}`,
      month: getMonthFromDate(item.date),
    }));
  } catch (error) {
    console.error("Error fetching Rajasthan Discom Forecast data:", error);
    return [];
  }
}

function applyBlockReplacements(
  data: RajasthanDiscomData[],
): RajasthanDiscomData[] {
  const replacementMappings: {
    targetDate: string;
    discom: string;
    blocks: number[];
    sourceDate: string;
  }[] = [
    {
      targetDate: "2024-12-10",
      discom: "Jodhpur",
      blocks: [82],
      sourceDate: "2024-12-09",
    },
    {
      targetDate: "2024-12-10",
      discom: "Ajmer",
      blocks: [82],
      sourceDate: "2024-12-09",
    },
  ];

  const modifiedData = [...data]; // Create a copy to avoid modifying original data

  replacementMappings.forEach(({ targetDate, discom, blocks, sourceDate }) => {
    blocks.forEach((block) => {
      const sourceRecord = data.find(
        (record) =>
          record.date === sourceDate &&
          record.block === block &&
          record.discom === discom,
      );

      if (sourceRecord) {
        modifiedData.forEach((record) => {
          if (
            record.date === targetDate &&
            record.block === block &&
            record.discom === discom
          ) {
            record.schedule_mw = sourceRecord.schedule_mw; // Apply the replacement
          }
        });
      }
    });
  });

  return modifiedData;
}

export async function getRajasthanGenerationData(
  startDate: string,
  endDate: string,
): Promise<RajasthanGenData[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getGenerationData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const uniqueDates = Array.from(
      new Set(response.map((item: any[]) => item[0])),
    );
    return response.map((item: any[]) => {
      const convertedData = convertToRajasthanGenData(item);
      return {
        ...convertedData,
        dateIndex:
          (uniqueDates.indexOf(convertedData.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
      };
    });
  } catch (error) {
    console.error("Error fetching Rajasthan Generation data:", error);
    return [];
  }
}

export async function getDemoRajasthanGenerationData(
  startDate: string,
  endDate: string,
): Promise<RajasthanGenData[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getGenerationData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    // derive unique dates & stations
    const uniqueDates = Array.from(
      new Set(response.map((item: any[]) => item[0])),
    );
    const uniqueStations = Array.from(
      new Set(
        response.map((item: any[]) => convertToRajasthanGenData(item).station),
      ),
    );
    const stationMap: Record<string, string> = {};
    uniqueStations.forEach(
      (st, i) => (stationMap[st as string] = `Station ${i + 1}`),
    );

    return response.map((item: any[]) => {
      const convertedData = convertToRajasthanGenData(item);
      return {
        ...convertedData,
        state: "Demo State",
        station: stationMap[convertedData.station] || convertedData.station,
        dateIndex:
          (uniqueDates.indexOf(convertedData.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
      };
    });
  } catch (error) {
    console.error("Error fetching Rajasthan Generation data:", error);
    return [];
  }
}

export async function getRajasthanIsgsData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanIsgs[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanIsgsData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    const response = toArray<RajasthanIsgs>(res);

    if (response.length === 0) return [];

    const uniqueDates = Array.from(new Set(response.map((item) => item.date)));
    return response.map((item) => {
      return {
        ...item,
        value_mwh: item.value_mw / 4,
        station: item.from_utility__name,
        month: getMonthFromDate(item.date),
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        from_gen__generation_source: item.generator_source,
      };
    });
  } catch (error) {
    console.error("Error fetching Rajasthan ISGS Data:", error);
    return [];
  }
}

export async function getDemoRajasthanIsgsData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanIsgs[]> {
  try {
    const response = (await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanIsgsData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    })) as RajasthanIsgs[];

    // derive unique dates, states & stations
    const uniqueDates = Array.from(new Set(response.map((item) => item.date)));
    const uniqueStates: string[] = Array.from(
      new Set(response.map((item: any) => item.state)),
    );
    const uniqueStations = Array.from(
      new Set(response.map((item) => item.from_utility__name)),
    );

    // build generic maps
    const stateMap: Record<string, string> = {};
    uniqueStates.forEach((s, i) => (stateMap[s] = `State ${i + 1}`));

    const stationMap: Record<string, string> = {};
    uniqueStations.forEach((s, i) => (stationMap[s] = `Station ${i + 1}`));

    return response.map((item) => ({
      ...item,
      state: stateMap[item.state] || item.state,
      station: stationMap[item.from_utility__name] || item.from_utility__name,
      from_utility__name:
        stationMap[item.from_utility__name] || item.from_utility__name,
      value_mwh: item.value_mw / 4,
      month: getMonthFromDate(item.date),
      dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      dates: `${startDate} to ${endDate}`,
      from_gen__generation_source: item.generator_source,
    }));
  } catch (error) {
    console.error("Error fetching Rajasthan ISGS Data:", error);
    return [];
  }
}

export async function getRajasthanIsgsIntervalData({
  startDate,
  endDate,
  interval = "date",
}: {
  startDate: string;
  endDate: string;
  interval?: string;
}): Promise<RajasthanIsgs[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/isgs-data-interval?start_date=${startDate}&end_date=${endDate}&interval=${interval}`,
      method: HttpMethod.GET,
    });
    // const mappings: RajMappings = await buildHttpResponse({
    //   endPoint: "rajasthan/getMappings",
    //   method: HttpMethod.GET,
    // });

    // Calculate uniqueDates from the response
    const uniqueDates = Array.from(
      new Set(response.map((item: any) => item.date)),
    );

    return response.map((item: any) => {
      return {
        ...item,
        from_gen__generation_source: item.generator_source,
        from_state: item.state,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      };
    });
  } catch (error) {
    console.error("Error fetching Rajasthan Interval ISGS Data:", error);
    return [];
  }
}

export async function getDemoRajasthanIsgsIntervalData({
  startDate,
  endDate,
  interval = "date",
}: {
  startDate: string;
  endDate: string;
  interval?: string;
}): Promise<RajasthanIsgs[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/isgs-data-interval?start_date=${startDate}&end_date=${endDate}&interval=${interval}`,
      method: HttpMethod.GET,
    });

    // calculate uniques
    const uniqueDates = Array.from(
      new Set(response.map((item: any) => item.date)),
    );
    const uniqueStates: string[] = Array.from(
      new Set(response.map((item: any) => item.state)),
    );

    // derive unique station names and build a generic map
    const uniqueStations: string[] = Array.from(
      new Set(response.map((item: any) => item.from_utility__name)),
    );
    const stationMap: Record<string, string> = {};
    uniqueStations.forEach((st, i) => {
      stationMap[st] = `Station ${i + 1}`;
    });

    // build state map
    const stateMap: Record<string, string> = {};
    uniqueStates.forEach((s, i) => {
      stateMap[s] = `State ${i + 1}`;
    });

    return response.map((item: any) => {
      return {
        ...item,
        state: stateMap[item.state] || item.state,
        from_state: stateMap[item.state] || item.state,
        station: stationMap[item.from_utility__name] || item.from_utility__name,
        from_utility__name:
          stationMap[item.from_utility__name] || item.from_utility__name,
        from_gen__generation_source: item.generator_source,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      };
    });
  } catch (error) {
    console.error("Error fetching Rajasthan Interval ISGS Data:", error);
    return [];
  }
}

export async function getRajasthanCollectiveData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanCollective[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getCollectiveData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return response;
  } catch (error) {
    console.error("Error fetching Rajasthan Collective Data:", error);
    return [];
  }
}

export async function getGnaTgnaData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanGnaTgna[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getGnaTgnaData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });
    return response.map((item: { slot: any }) => {
      return {
        ...item,
        time_block: item.slot,
      };
    });
  } catch (error) {
    console.error("Error fetching GnaTgna data:", error);
    return [];
  }
}

export async function getDemoGnaTgnaData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajasthanGnaTgna[]> {
  try {
    const response: any[] = await buildHttpResponse({
      endPoint: `rajasthan/getGnaTgnaData?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    // derive uniques for each field
    const uniqueFromStates = Array.from(
      new Set(response.map((item) => item.from_state__name)),
    );
    const uniqueFromEntityStates = Array.from(
      new Set(response.map((item) => item.from_utility__entity_state__name)),
    );
    const uniqueFromUtilities = Array.from(
      new Set(response.map((item) => item.from_utility__name)),
    );
    const uniqueToStates = Array.from(
      new Set(response.map((item) => item.to_state__name)),
    );
    const uniqueToUtilities = Array.from(
      new Set(response.map((item) => item.to_utility__name)),
    );
    const uniqueTraders = Array.from(
      new Set(response.map((item) => item.trader__name)),
    );

    // build generic maps
    const fromStateMap: Record<string, string> = {};
    uniqueFromStates.forEach((v, i) => {
      fromStateMap[v] = `State ${i + 1}`;
    });

    const fromEntityStateMap: Record<string, string> = {};
    uniqueFromEntityStates.forEach((v, i) => {
      fromEntityStateMap[v] = `State ${i + 1}`;
    });

    const fromUtilityMap: Record<string, string> = {};
    uniqueFromUtilities.forEach((v, i) => {
      fromUtilityMap[v] = `Utility ${i + 1}`;
    });

    const toStateMap: Record<string, string> = {};
    uniqueToStates.forEach((v, i) => {
      toStateMap[v] = `State ${i + 1}`;
    });

    const toUtilityMap: Record<string, string> = {};
    uniqueToUtilities.forEach((v, i) => {
      toUtilityMap[v] = `Utility ${i + 1}`;
    });

    const traderMap: Record<string, string> = {};
    uniqueTraders.forEach((v, i) => {
      traderMap[v] = `Trader ${i + 1}`;
    });

    return response.map((item) => ({
      ...item,
      time_block: item.slot,
      from_state__name:
        fromStateMap[item.from_state__name] || item.from_state__name,
      from_utility__entity_state__name:
        fromEntityStateMap[item.from_utility__entity_state__name] ||
        item.from_utility__entity_state__name,
      from_utility__name:
        fromUtilityMap[item.from_utility__name] || item.from_utility__name,
      to_state__name: toStateMap[item.to_state__name] || item.to_state__name,
      to_utility__name:
        toUtilityMap[item.to_utility__name] || item.to_utility__name,
      trader__name: traderMap[item.trader__name] || item.trader__name,
    }));
  } catch (error) {
    console.error("Error fetching GnaTgna data:", error);
    return [];
  }
}
export async function createRajProcurementRecord(
  formData: any,
): Promise<string> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/procurement/create`,
      method: HttpMethod.POST,
      body: formData,
    });
    return response.message;
  } catch (error) {
    console.error("Error fetching GnaTgna data:", error);
    return "Inserting error";
  }
}

export async function getRajFileTypes(): Promise<string[]> {
  const response = await buildHttpResponse({
    endPoint: "rajasthan/get-file-types",
    method: HttpMethod.GET,
  });
  return response;
}

export async function uploadRajFile(formData: any): Promise<string> {
  const response = await buildMultipartHttpResponse({
    endPoint: "rajasthan/upload-file", // Replace with your endpoint
    method: HttpMethod.POST,
    formData,
  });
  return response.message;
}

export async function getRajReData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<ReEnergyData[]> {
  const response: ReEnergyData[] = await buildHttpResponse({
    endPoint: `rajasthan/getRenewableGeneration?start_date=${startDate}&end_date=${endDate}`, // Replace with your endpoint
    method: HttpMethod.GET,
  });

  const uniqueDates = Array.from(new Set(response.map((item) => item.date)));
  return response.map((item) => {
    return {
      ...item,
      value_mwh: item.value_mw / 4,
      dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      dates: `${startDate} to ${endDate}`,
      month: getMonthFromDate(item.date),
      energy_type: item.energy_type__name,
      time_block: item.slot,
    };
  });
}
export async function getProcurementRecord(
  page: any,
  perpage: any,
): Promise<RajProcurementRecord[]> {
  try {
    const response = await fetch(
      `https://datahub.gna.energy/rajasthan_procurement?page=${page}&per_page=${perpage}`,
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error("Error fetching Procurement Record :", error);
    return [];
  }
}

export async function getRajOperatingPar(): Promise<RajGenParams[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getRajOperatingPar`,
      method: HttpMethod.GET,
    });
    return response;
  } catch (error) {
    console.error("Error fetching Rajasthan Operating Parameters data:", error);
    return [];
  }
}

export async function getRajOutageData(startDate: string, endDate: string) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/getMachinePositionDetailed?from_date=${startDate}&to_date=${endDate}`,
    method: HttpMethod.GET,
  });
  return response;
}

export async function getDemoRajOperatingPar(): Promise<RajGenParams[]> {
  try {
    const response = (await buildHttpResponse({
      endPoint: `rajasthan/getRajOperatingPar`,
      method: HttpMethod.GET,
    })) as RajGenParams[];

    // derive uniques
    const uniqueGenerators = Array.from(
      new Set(response.map((item) => item.generator)),
    );

    // build generic map
    const generatorMap: Record<string, string> = {};
    uniqueGenerators.forEach((g, i) => {
      generatorMap[g] = `Generator ${i + 1}`;
    });

    // apply mapping
    return response.map((item) => ({
      ...item,
      generator: generatorMap[item.generator] || item.generator,
    }));
  } catch (error) {
    console.error("Error fetching Rajasthan Operating Parameters data:", error);
    return [];
  }
}

export async function getRajFiles(): Promise<RajasthanUploadFile[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getRajUploadedFile`,
      method: HttpMethod.GET,
    });
    return response;
  } catch (error) {
    console.error("Error fetching Rajasthan Files data:", error);
    return [];
  }
}

export async function rajChatGptRes({
  query,
  thread_token,
}: {
  query: string;
  thread_token: string;
}): Promise<{ sender: String; content: String }> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/handeUserQuery?query=${query}&thread_token=${thread_token}`,
    method: HttpMethod.GET,
  });
  return response;
}

export async function getRajGenProfileData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajGenProfile[]> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/getGenProfileData?start_date=${startDate}&end_date=${endDate}`, // Replace with your endpoint
    method: HttpMethod.GET,
  });
  return response;
}
export async function getRajSurplusData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajSurplusDeficitData[]> {
  const response = await buildHttpResponse({
    endPoint: `/rajasthan/getSurDefProfile?start_date=${startDate}&end_date=${endDate}`,
    method: HttpMethod.GET,
  });
  return response.map((item: any) => {
    return {
      ...item,
      block: item.time_block,
    };
  });
}

export async function getSurDefProfile({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajGenProfile[]> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/getSurDefProfile?start_date=${startDate}&end_date=${endDate}`, // Replace with your endpoint
    method: HttpMethod.GET,
  });
  return response;
}

export async function getRajDeep({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajProcurement[]> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/getRajDeep?start_date=${startDate}&end_date=${endDate}`, // Replace with your endpoint
    method: HttpMethod.GET,
  });
  return response;
}

export async function getRajIntraGen({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajProcurement[]> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/getRajIntraGen?start_date=${startDate}&end_date=${endDate}`, // Replace with your endpoint
    method: HttpMethod.GET,
  });
  return response;
}

export async function getRajIsgs({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajProcurement[]> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/getRajIsgs?start_date=${startDate}&end_date=${endDate}`, // Replace with your endpoint
    method: HttpMethod.GET,
  });
  return response;
}

export async function getRajTamData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajTamData[]> {
  const response: RajTamData[] = await buildHttpResponse({
    endPoint: `rajasthan/getRajNoarData?start_date=${startDate}&end_date=${endDate}`, // Replace with your endpoint
    method: HttpMethod.GET,
  });
  return response;
}

export async function getRajNoar({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajTamData[]> {
  const response: RajTamData[] = await buildHttpResponse({
    endPoint: `rajasthan/getRajNoarData?start_date=${startDate}&end_date=${endDate}`, // Replace with your endpoint
    method: HttpMethod.GET,
  });
  return response;
}

export async function getRajBankingData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): Promise<RajBankingData[]> {
  const response: RajBankingData[] = await buildHttpResponse({
    endPoint: `rajasthan/getRajBankingData?start_date=${startDate}&end_date=${endDate}`, // Replace with your endpoint
    method: HttpMethod.GET,
  });
  return response.map((item) => {
    return {
      ...item,
      mwh: item.mwh,
    };
  });
}

export async function getRajEntity() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/entities`,
    method: HttpMethod.GET,
  });
  return response;
}
export async function getRajTrader() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/traders`,
    method: HttpMethod.GET,
  });
  return response;
}

export async function getDemoRajEntity() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/entities`,
    method: HttpMethod.GET,
  });
  return response;
}
export async function getDemoRajTrader() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/traders`,
    method: HttpMethod.GET,
  });
  return response;
}

export async function PostRajTransaction(data: any) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/transactions/create/`,
    method: HttpMethod.POST,
    body: data,
  });
  return response;
}
export async function PostBulkRajTransaction(data: any) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/transaction/bulk_create`,
    method: HttpMethod.POST,
    body: data,
  });
  return response;
}
export async function getRajTransaction() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/transactions/`,
    method: HttpMethod.GET,
  });
  return response;
}
export async function deleteRajTransaction(transaction_id: number) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/transactions/${transaction_id}/delete/`,
    method: HttpMethod.DELETE,
  });
  return response;
}
export async function upadteRajTransaction(transaction_id: number, data: any) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/transactions/${transaction_id}/update/`,
    method: HttpMethod.PUT,
    body: data,
  });
  return response;
}

export async function getRajSchedulingData({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/getRajSchedulingData?start_date=${startDate}&end_date=${endDate}`,
    method: HttpMethod.GET,
  });
  return response;
}

export async function getRajasthanOutageData(
  startDate: string,
  endDate: string,
  interval = "date",
) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/getMachinePositionInterval?start_date=${startDate}&end_date=${endDate}&interval=${interval}`,
    method: HttpMethod.GET,
  });

  // Get unique dates from the response
  const uniqueDates = Array.from(
    new Set(response.map((item: any) => item.date)),
  );

  return response.map((item: any) => {
    return {
      ...item,
      dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      dates: `${startDate} to ${endDate}`,
      outage_date: item.date,
      outage_time: item.slot,
    };
  });
}

export async function getDemoRajasthanOutageData(
  startDate: string,
  endDate: string,
  interval = "date",
) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/getMachinePositionInterval?start_date=${startDate}&end_date=${endDate}&interval=${interval}`,
    method: HttpMethod.GET,
  });

  // Get unique dates from the response
  const uniqueDates = Array.from(
    new Set(response.map((item: any) => item.date)),
  );

  // derive unique station names and build a generic map
  const uniqueStations: string[] = Array.from(
    new Set<string>(response.map((item: any) => item.station_name as string)),
  );
  const stationMap: Record<string, string> = {};
  uniqueStations.forEach((s, i) => {
    stationMap[s] = `Station ${i + 1}`;
  });

  return response.map((item: any) => {
    return {
      ...item,
      // apply station_name mapping
      station_name: stationMap[item.station_name] || item.station_name,
      dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      dates: `${startDate} to ${endDate}`,
      outage_date: item.date,
      outage_time: item.slot,
    };
  });
}

export async function fetchRvpnData(
  startDate: string,
  endDate: string,
): Promise<RajashtanRrvpn[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/getRajasthanNreb?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const columns: string[] = res.columns ?? [];
    const rows: any[][] = res.data ?? [];

    if (rows.length === 0) return [];

    // Map array rows to objects using column names
    const rawData = rows.map((row) => {
      const obj: Record<string, any> = {};
      columns.forEach((col, i) => {
        obj[col] = row[i];
      });
      return obj;
    });

    // Build uniqueDates for dateIndex labeling
    const uniqueDates = Array.from(new Set(rawData.map((item) => item.date)));

    return rawData.map((item) => ({
      ...item,
      date: item.date, // "2026-01-04"
      drawal_mw: item.drawal_mw, // explicitly added for RajashtanRrvpn
      block: item.slot, // 1, 2, 3 ...
      slot_id: item.slot, // keep for compatibility
      rvpnl_drawal_mw: item.drawal_mw, // 9847.95
      rvpnl_drawal_mwh: item.drawal_mw / 4, // energy per 15-min block
      rvpnl_drawal_lu: item.drawal_mw / 400, // load unit
      dateIndex: `${uniqueDates.indexOf(item.date) + 1} Day`,
    })) as RajashtanRrvpn[];
  } catch (error) {
    console.error("Error fetching Rvpn data:", error);
    return [];
  }
}
// MOD START POST Session
// MOD Ruvital START POST Session
export async function fetchgetModSession(startdate: string) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/startSession?date=${startdate}`,
    method: HttpMethod.POST,
    body: {
      date: startdate,
    },
  });
  return response;
}

export async function getModSession() {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getSession`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// MOD Stage data
export async function ModRuvnlStagePost(stage: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/postStage`,
    method: HttpMethod.POST,
    body: {
      stage: stage,
      session: session,
    },
  });
  return response;
}
export async function fetchModRuvnlArchive(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getPreviousSession?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModRuvnlActivateSession(session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/activateSession`,
    method: HttpMethod.POST,
    body: {
      session: session,
    },
    noCache: true,
  });
  return response;
}
// MOD DAM default data
export async function fetchModDamDefault(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getDefaultData/dam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
  });
  return response;
}
// MOD GDAM default data
export async function fetchModGDamDefault(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getDefaultData/gdam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function ModRuvnlAggregatedLoadData(
  ModAggregatedData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/postAggregatedLoadPrice`,
    method: HttpMethod.POST,
    body: ModAggregatedData,
  });

  return response as ApiResponse;
}

export async function fetchModRuvnlGDamData(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getData/gdam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModGDamData(ModGDamdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postGDam`,
    method: HttpMethod.POST,
    noCache: true,
    body: ModGDamdata,
  });

  return response as ApiResponse;
}
export async function fetchModGDamData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getData/gdam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchModDemandDefault(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getDemandDefault?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
  });
  return response;
}
// upload file POST DAM RTM DEMAND
export async function ModRuvnlDamData(ModDamdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/postDam`,
    method: HttpMethod.POST,
    body: ModDamdata,
  });

  return response as ApiResponse;
}

export async function ModRtmData(ModRtmdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postRtm`,
    method: HttpMethod.POST,
    body: ModRtmdata,
  });

  return response as ApiResponse;
}
export async function ModRuvnlDemandData(
  ModDemanddata: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/postDemand`,
    method: HttpMethod.POST,
    body: ModDemanddata,
  });

  return response as ApiResponse;
}
// MOD DAM fetch Data
export async function fetchModDamData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getData/dam?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModDemandData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getDemand?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModRtmDefault(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getDefaultData/rtm?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
  });
  return response;
}
// MOD RTM fetch Data
export async function fetchModRtmData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getData/rtm?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModBulkUploadFile(formData: any): Promise<{
  automationMod_files: Array<{ file_url: string }>;
}> {
  const response = await buildMultipartHttpResponse({
    endPoint: "mod_cspdcl/automation",
    method: HttpMethod.POST,
    formData,
  });
  return response;
}

export async function fetchModRuvnlTableChartData(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getMODChart?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModRuvnlTableData(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getMOD?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModRuvnlUpdateData(
  ModUpdatedata: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/postMOD`,
    method: HttpMethod.POST,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}
export async function ModRuvnlUpdateBidData(
  ModUpdatedata: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/postBid`,
    method: HttpMethod.POST,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}

export async function fetchModRuvnlBidTableData(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getBid?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModRuvnlBidDownload(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getBidDownload?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });

  return response as ApiResponse;
}

export async function fetchModRuvnlSimulationSummary(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getSimulatorGenChart?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModRuvnlSimulationTopBuyer(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getSimulatorGenPrice?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModRuvnlSimulationCostPower(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getSimulatorCostPower?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchModRuvnlSimulationSaleRevenue(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getSimulatorSalesRevenue?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function getModRuvnlBidSimulationData(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/getSimulatorBid?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

// GNA/TGNA Optimizer Session

export async function fetchOptimizerSession(
  startDate: string,
  endDate: string,
) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/start-gna-session?start_date=${startDate}&end_date=${endDate}`,
    method: HttpMethod.POST,
    body: {
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response;
}
export async function getGNAOptimizerSession() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/get-gna-session`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchOptimizerSummary() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/generation-profile-slotwise`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimalUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-optimal-utilisation-slotwise`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchSimulatedUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-simulated-utilisation-slotwise`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimizerMetrics() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-utilisation-metrics`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimizerStatistics() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-utilisation-statistics`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimizerGeneration() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/generation-profile-cummulative`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimizerSguGeneration() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/generation-profile`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function OptimizerSguUpdateData(
  Updatedata: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/generation-profile`,
    method: HttpMethod.POST,
    body: Updatedata,
  });

  return response as ApiResponse;
}
export async function OptimizerCurrentUpdateData(
  Updatedata: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-parameters`,
    method: HttpMethod.POST,
    body: Updatedata,
  });

  return response as ApiResponse;
}

export async function fetchOptimizerCurrentOptimisation() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-parameters`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchDailyOptimalUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-optimal-utilisation-daily`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchDailysimulatedUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-simulated-utilisation-daily`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchMonthlysimulatedUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-simulated-utilisation-monthly`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function OptimizerGnaProceed() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-proceed`,
    method: HttpMethod.POST,
  });

  return response as ApiResponse;
}

export async function fetchRuvnlLgbrTableData(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/get-lgbr?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchRuvnlLgbrGetData(
  type: string | null,
  time_slot: number | null,
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_ruvnl/get-lgbr-generator?date=${startdate}&session=${session}&type=${type}&time_slot=${time_slot}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchMonthlyOptimalUtilisation() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/gna-optimal-utilisation-monthly`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function getRajasthanGssRestrictedAreaSummaryMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-slot-wise-metrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = response?.data ?? response;
    if (!res || !Array.isArray(res) || res.length === 0) return [];

    const mappedData = res.map((item: any) => {
      return {
        slot: item.slot,
        discom: item.discom,
        station: item.station,
        area: item.station,
        time_string: item.time_string,
        min_load: parseFloat(item.min_load?.toString() || "0"),
        date_of_min_load: item.date_of_min_load,
        max_load: parseFloat(item.max_load?.toString() || "0"),
        date_of_max_load: item.date_of_max_load,
        avg_load: parseFloat(item.avg_load?.toString() || "0"),
        mode_load: parseFloat(item.mode_load?.toString() || "0"),
        mode_load_class_dynamic: parseFloat(
          item.mode_load_class_dynamic?.toString() || "0",
        ),
        mode_load_class_static: parseFloat(
          item.mode_load_class_static?.toString() || "0",
        ),
        mode_load_formula: parseFloat(
          item.mode_load_formula?.toString() || "0",
        ),
        mode_load_window_formula: parseFloat(
          item.mode_load_window_formula?.toString() || "0",
        ),
      };
    });

    return mappedData;
  } catch (error) {
    console.error("[AREA API] Error fetching data:", error);

    return [];
  }
}
export async function getRajasthanGssRestrictedSummaryMetricsTotal(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-slot-wise-metrics-total?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    if (!res) return [];

    // API returns { success, count, data: [...] }
    const rows = Array.isArray(res) ? res : (res.data ?? []);

    if (rows.length === 0) return [];

    return rows.map((item: any) => ({
      slot: item.slot,
      time_string: item.time_string,
      min_load: parseFloat(item.min_load?.toString() || "0"),
      date_of_min_load: item.date_of_min_load,
      max_load: parseFloat(item.max_load?.toString() || "0"),
      date_of_max_load: item.date_of_max_load,
      avg_load: parseFloat(item.avg_load?.toString() || "0"),
      mode_load: parseFloat(item.mode_load?.toString() || "0"),
      mode_load_class_dynamic: parseFloat(
        item.mode_load_class_dynamic?.toString() || "0",
      ),
      mode_load_class_static: parseFloat(
        item.mode_load_class_static?.toString() || "0",
      ),
      mode_load_formula: parseFloat(item.mode_load_formula?.toString() || "0"),
      mode_load_window_formula: parseFloat(
        item.mode_load_window_formula?.toString() || "0",
      ),
    }));
  } catch (error) {
    console.error("[TOTAL API] Error fetching data:", error);
    return [];
  }
}
// Discom API - Returns discom-level aggregated data
export async function getRajasthanGssRestrictedSummaryMetricsDiscom(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-slot-wise-metrics-discom?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = response?.data ?? response;
    if (!res || !Array.isArray(res) || res.length === 0) return [];

    return res.map((item: any) => ({
      slot: item.slot,
      discom: item.discom,
      time_string: item.time_string,
      min_load: parseFloat(item.min_load?.toString() || "0"),
      date_of_min_load: item.date_of_min_load,
      max_load: parseFloat(item.max_load?.toString() || "0"),
      date_of_max_load: item.date_of_max_load,
      avg_load: parseFloat(item.avg_load?.toString() || "0"),
      mode_load: parseFloat(item.mode_load?.toString() || "0"),
      mode_load_class_dynamic: parseFloat(
        item.mode_load_class_dynamic?.toString() || "0",
      ),
      mode_load_class_static: parseFloat(
        item.mode_load_class_static?.toString() || "0",
      ),
      mode_load_formula: parseFloat(item.mode_load_formula?.toString() || "0"),
      mode_load_window_formula: parseFloat(
        item.mode_load_window_formula?.toString() || "0",
      ),
    }));
  } catch (error) {
    console.error("[DISCOM API] Error fetching data:", error);

    return [];
  }
}
// District API - Returns district-wise data (discom + district)
export async function getRajasthanGssLoadMetricsRestrictedDiscomDistrict(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-metrics-discom-district?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = response?.data ?? response;
    if (!res || !Array.isArray(res) || res.length === 0) return [];

    return res.map((item: any) => ({
      slot: item.slot,
      discom: item.discom,
      district: item.district,
      time_string: item.time_string,
      min_load: parseFloat(item.min_load?.toString() || "0"),
      date_of_min_load: item.date_of_min_load,
      max_load: parseFloat(item.max_load?.toString() || "0"),
      date_of_max_load: item.date_of_max_load,
      avg_load: parseFloat(item.avg_load?.toString() || "0"),
      mode_load: parseFloat(item.mode_load?.toString() || "0"),
      mode_load_class_dynamic: parseFloat(
        item.mode_load_class_dynamic?.toString() || "0",
      ),
      mode_load_class_static: parseFloat(
        item.mode_load_class_static?.toString() || "0",
      ),
      mode_load_formula: parseFloat(item.mode_load_formula?.toString() || "0"),
      mode_load_window_formula: parseFloat(
        item.mode_load_window_formula?.toString() || "0",
      ),
    }));
  } catch (error) {
    console.error("[DISTRICT API] Error fetching data:", error);

    return [];
  }
}
export async function getChhattisgarhDemandMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `chhattisgarh/getDemandMetrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = Array.isArray(response) ? response : [];
    return data.map((item: any) => ({
      slot: item.slot,
      time_string: item.time_string,
      min_demand: parseFloat(item.min_demand?.toString() ?? "0"),
      date_of_min: item.date_of_min,
      max_demand: parseFloat(item.max_demand?.toString() ?? "0"),
      date_of_max: item.date_of_max,
      avg_demand: parseFloat(item.avg_demand?.toString() ?? "0"),
      mode_demand: parseFloat(item.mode_demand?.toString() ?? "0"),
      // Preferred mode field — chart derivation uses this over mode_demand when present
      mode_demand_window_formula: parseFloat(
        item.mode_demand_window_formula?.toString() ?? "0",
      ),
    }));
  } catch (error) {
    console.error("Error fetching Chhattisgarh Demand Metrics data:", error);
    return [];
  }
}
export async function getChhattisgarhForecastMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `chhattisgarh/getForecastMetrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = Array.isArray(response) ? response : [];
    // API returns TWO rows per slot: one for T+1 and one for T+2.
    // We preserve forecast_type so callers can split them into separate Maps.
    // mode_demand_window_formula is kept because the chart prefers it over mode_demand.
    return data.map((item: any) => ({
      slot: item.slot,
      forecast_type: item.forecast_type, // "T+1" or "T+2"
      time_string: item.time_string,
      min_demand: parseFloat(item.min_demand?.toString() ?? "0"),
      max_demand: parseFloat(item.max_demand?.toString() ?? "0"),
      avg_demand: parseFloat(item.avg_demand?.toString() ?? "0"),
      mode_demand: parseFloat(item.mode_demand?.toString() ?? "0"),
      mode_demand_window_formula: parseFloat(
        item.mode_demand_window_formula?.toString() ?? "0",
      ),
      date_of_min: item.date_of_min,
      date_of_max: item.date_of_max,
    }));
  } catch (error) {
    console.error("Error fetching Chhattisgarh Forecast Metrics data:", error);
    return [];
  }
}

export async function getDiscomDrawalScheduleMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getDiscomDrawalScheduleMetrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = Array.isArray(response) ? response : (response?.data ?? []);

    return res.map((item: any) => ({
      slot: item.slot,
      discom: item.discom,
      time_string: item.time_string,
      min_drawal: parseFloat(item.min_drawal?.toString() || "0"),
      max_drawal: parseFloat(item.max_drawal?.toString() || "0"),
      avg_drawal: parseFloat(item.avg_drawal?.toString() || "0"),
      mode_drawal_window_formula: parseFloat(
        item.mode_drawal?.toString() || "0",
      ), // ✅ API field is mode_drawal
      min_schedule: parseFloat(item.min_schedule?.toString() || "0"),
      max_schedule: parseFloat(item.max_schedule?.toString() || "0"),
      avg_schedule: parseFloat(item.avg_schedule?.toString() || "0"),
      mode_schedule_window_formula: parseFloat(
        item.mode_schedule?.toString() || "0",
      ), // ✅ API field is mode_schedule
      date_of_min_drawal: item.date_of_min_drawal,
      date_of_max_drawal: item.date_of_max_drawal,
      date_of_min_schedule: item.date_of_min_schedule,
      date_of_max_schedule: item.date_of_max_schedule,
    }));
  } catch (error) {
    console.error("Error fetching Discom Drawal Schedule Metrics data:", error);
    return [];
  }
}

export async function getChhattisgarhDailyPeak(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `chhattisgarh/getDailyPeakDemand?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(response?.data) ? response.data : [];

    if (data.length === 0) return [];

    return data.map((item: any) => {
      const morningData = item.formatted_data?.find(
        (fd: any) => fd.time_period === "Morning",
      );
      const eveningData = item.formatted_data?.find(
        (fd: any) => fd.time_period === "Evening",
      );

      const morningMax = parseFloat(morningData?.max_demand?.toString() ?? "0");
      const eveningMax = parseFloat(eveningData?.max_demand?.toString() ?? "0");
      return {
        date: item.date,
        peak_mw: parseFloat(item.daily_peak_demand_value_mw?.toString() ?? "0"),
        peak_slot:
          morningMax >= eveningMax
            ? (morningData?.max_demand_slot ?? "")
            : (eveningData?.max_demand_slot ?? ""),
        average_mw: parseFloat(
          item.avg_daily_peak_demand_value_mw?.toString() ?? "0",
        ),
        morning_peak_mw: morningMax,
        morning_peak_slot: morningData?.max_demand_slot ?? "",
        evening_peak_mw: eveningMax,
        evening_peak_slot: eveningData?.max_demand_slot ?? "",
      };
    });
  } catch (error) {
    console.error("Error fetching Chhattisgarh daily peak data:", error);
    return [];
  }
}
export async function getChhattisgarhDailyForecastedPeak(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `chhattisgarh/getDailyPeakForecast?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = Array.isArray(response?.data) ? response.data : [];

    if (data.length === 0) return [];

    // Group by date, separate T+1 and T+2
    const byDate = new Map<string, { d1?: any; d2?: any }>();
    for (const item of data) {
      const entry = byDate.get(item.date) ?? {};
      if (item.forecast_type === "T+1") entry.d1 = item;
      else if (item.forecast_type === "T+2") entry.d2 = item;
      byDate.set(item.date, entry);
    }

    return Array.from(byDate.entries()).map(([date, { d1, d2 }]) => {
      const morningD1 = d1?.formatted_data?.find(
        (fd: any) => fd.time_period === "Morning",
      );
      const eveningD1 = d1?.formatted_data?.find(
        (fd: any) => fd.time_period === "Evening",
      );
      const morningD2 = d2?.formatted_data?.find(
        (fd: any) => fd.time_period === "Morning",
      );
      const eveningD2 = d2?.formatted_data?.find(
        (fd: any) => fd.time_period === "Evening",
      );

      // Derive day-peak slot from whichever period matches the daily peak
      const d1PeakSlot =
        d1?.daily_peak_forecasted_demand_mw === eveningD1?.max_forecasted_demand
          ? (eveningD1?.max_forecasted_demand_block ?? "")
          : (morningD1?.max_forecasted_demand_block ?? "");
      const d2PeakSlot =
        d2?.daily_peak_forecasted_demand_mw === eveningD2?.max_forecasted_demand
          ? (eveningD2?.max_forecasted_demand_block ?? "")
          : (morningD2?.max_forecasted_demand_block ?? "");

      return {
        date,
        // T+1 fields
        peak_mw: parseFloat(
          d1?.daily_peak_forecasted_demand_mw?.toString() ?? "0",
        ),
        average_mw: parseFloat(
          d1?.avg_daily_forecasted_demand_mw?.toString() ?? "0",
        ),
        peak_slot: d1PeakSlot,
        morning_peak_mw: parseFloat(
          morningD1?.max_forecasted_demand?.toString() ?? "0",
        ),
        morning_peak_slot: morningD1?.max_forecasted_demand_block ?? "",
        evening_peak_mw: parseFloat(
          eveningD1?.max_forecasted_demand?.toString() ?? "0",
        ),
        evening_peak_slot: eveningD1?.max_forecasted_demand_block ?? "",
        // T+2 fields
        d2_peak_mw: parseFloat(
          d2?.daily_peak_forecasted_demand_mw?.toString() ?? "0",
        ),
        d2_average_mw: parseFloat(
          d2?.avg_daily_forecasted_demand_mw?.toString() ?? "0",
        ),
        d2_peak_slot: d2PeakSlot,
        d2_morning_peak_mw: parseFloat(
          morningD2?.max_forecasted_demand?.toString() ?? "0",
        ),
        d2_morning_peak_slot: morningD2?.max_forecasted_demand_block ?? "",
        d2_evening_peak_mw: parseFloat(
          eveningD2?.max_forecasted_demand?.toString() ?? "0",
        ),
        d2_evening_peak_slot: eveningD2?.max_forecasted_demand_block ?? "",
      };
    });
  } catch (error) {
    console.error("Error fetching Chhattisgarh daily peak data:", error);
    return [];
  }
}
export async function getDailyDiscomDrawalSchedule(
  startDate: string,
  endDate: string,
): Promise<DailyDiscomDrawalSchedule[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getDailyDiscomDrawalSchedule?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    // API wraps data in { success, data: [...] }
    const data: any[] = res.data ?? (res as any)?.data ?? [];

    if (!Array.isArray(data) || data.length === 0) return [];

    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));
    const parseNum = (val: any) => parseFloat(val?.toString() ?? "0");

    return data.map((item) => {
      const morningData = item.formatted_data?.find(
        (fd: any) => fd.time_period === "Morning",
      );
      const eveningData = item.formatted_data?.find(
        (fd: any) => fd.time_period === "Evening",
      );

      // Derive daily peak = max of morning peak and evening peak (since root has no summary fields)
      const daily_peak_drawal_mw = Math.max(
        parseNum(morningData?.max_drawal),
        parseNum(eveningData?.max_drawal),
      );
      const daily_peak_schedule_mw = Math.max(
        parseNum(morningData?.max_schedule),
        parseNum(eveningData?.max_schedule),
      );
      // Derive daily average = average of morning avg and evening avg
      const avg_daily_drawal_mw =
        (parseNum(morningData?.avg_drawal) +
          parseNum(eveningData?.avg_drawal)) /
        2;
      const avg_daily_schedule_mw =
        (parseNum(morningData?.avg_schedule) +
          parseNum(eveningData?.avg_schedule)) /
        2;

      return {
        ...item,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
        date: item.date,
        discom: item.discom,
        // Derived daily summary
        daily_peak_drawal_mw,
        daily_peak_schedule_mw,
        avg_daily_drawal_mw,
        avg_daily_schedule_mw,
        // Peak/average aliases used by the component
        peak_drawal_mw: daily_peak_drawal_mw,
        peak_schedule_mw: daily_peak_schedule_mw,
        average_drawal_mw: avg_daily_drawal_mw,
        average_schedule_mw: avg_daily_schedule_mw,
        // Morning peak
        morning_peak_drawal_mw: parseNum(morningData?.max_drawal),
        morning_peak_drawal_slot: morningData?.max_drawal_slot ?? "",
        morning_peak_schedule_mw: parseNum(morningData?.max_schedule),
        morning_peak_schedule_slot: morningData?.max_schedule_slot ?? "",
        // Evening peak
        evening_peak_drawal_mw: parseNum(eveningData?.max_drawal),
        evening_peak_drawal_slot: eveningData?.max_drawal_slot ?? "",
        evening_peak_schedule_mw: parseNum(eveningData?.max_schedule),
        evening_peak_schedule_slot: eveningData?.max_schedule_slot ?? "",
        // Preserve formatted_data as-is (already has correct shape)
        formatted_data: item.formatted_data ?? [],
      };
    });
  } catch (error) {
    console.error("Error fetching Rajasthan Discom drawal schedule:", error);
    return [];
  }
}

export async function getRestrictedGssLoadDailyDiscom(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-daily-discom?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = toArray<any>(res);
    if (!data || data.length === 0) return [];

    // Pre-compute unique dates once
    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));
    const dateIndexMap = new Map(
      uniqueDates.map((date, idx) => [date, `${idx + 1} Day`]),
    );
    const dateRange = `${startDate} to ${endDate}`;

    const parseNum = (val: any) => parseFloat(val?.toString() || "0");

    // Use a more efficient mapping approach
    return data.map((item) => {
      const formattedData = item.formatted_data || [];
      const eveningData =
        formattedData.find((fd: any) => fd.time_period === "Evening") || {};
      const morningData =
        formattedData.find((fd: any) => fd.time_period === "Morning") || {};

      return {
        date: item.date,
        discom: item.discom,
        dateIndex: dateIndexMap.get(item.date),
        dates: dateRange,
        month: getMonthFromDate(item.date),
        avg_daily_load_mw: parseNum(item.avg_daily_load_mw),
        daily_peak_load_mw: parseNum(item.daily_peak_load_mw),

        // Evening peak values
        evening_peak_load_mw: parseNum(eveningData.max_load),
        evening_peak_load_slot: eveningData.max_load_slot,
        evening_min_load_mw: parseNum(eveningData.min_load),
        evening_min_load_slot: eveningData.min_load_slot,
        evening_avg_load_mw: parseNum(eveningData.avg_load),

        // Morning peak values
        morning_peak_load_mw: parseNum(morningData.max_load),
        morning_peak_load_slot: morningData.max_load_slot,
        morning_min_load_mw: parseNum(morningData.min_load),
        morning_min_load_slot: morningData.min_load_slot,
        morning_avg_load_mw: parseNum(morningData.avg_load),

        formatted_data: [
          {
            time_period: "Evening",
            max_load: parseNum(eveningData.max_load),
            min_load: parseNum(eveningData.min_load),
            avg_load: parseNum(eveningData.avg_load),
            max_load_slot: eveningData.max_load_slot,
            min_load_slot: eveningData.min_load_slot,
          },
          {
            time_period: "Morning",
            max_load: parseNum(morningData.max_load),
            min_load: parseNum(morningData.min_load),
            avg_load: parseNum(morningData.avg_load),
            max_load_slot: morningData.max_load_slot,
            min_load_slot: morningData.min_load_slot,
          },
        ],
      };
    });
  } catch (error) {
    console.error("Error fetching GSS Load Daily Discom data:", error);
    return [];
  }
}

export async function getRestrictedMonthlyDiscomGssLoad(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-monthly-discom?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    return data.map((item: any) => {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const yearShort = item.year.toString().slice(-2);
      const monthStr = `${monthNames[item.month - 1]}-${yearShort}`;

      return {
        month: monthStr,
        year: item.year,
        discom: item.discom,

        // Average load
        avg_daily_load_mw: parseFloat(item.average_load_mw?.toString() || "0"),

        // Overall peak load (used as daily_peak_load_mw)
        daily_peak_load_mw: parseFloat(item.peak_load_mw?.toString() || "0"),
        date_of_peak_load: item.date_of_peak_load,
        timeblock_of_peak_load: item.timeblock_of_peak_load,

        // Morning peak load
        morning_peak_load_mw: parseFloat(
          item.morning_peak_load_mw?.toString() || "0",
        ),
        date_of_morning_peak_load: item.date_of_morning_peak_load,
        timeblock_of_morning_peak_load: item.timeblock_of_morning_peak_load,

        // Evening peak load
        evening_peak_load_mw: parseFloat(
          item.evening_peak_load_mw?.toString() || "0",
        ),
        date_of_evening_peak_load: item.date_of_evening_peak_load,
        timeblock_of_evening_peak_load: item.timeblock_of_evening_peak_load,
      };
    });
  } catch (error) {
    console.error("Error fetching Monthly Discom GSS Load data:", error);
    return [];
  }
}

export async function getGssLoadRestrictedDistrictDailyDiscom(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-daily-discom-district?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = toArray<any>(res);
    if (!data || data.length === 0) return [];

    // Get unique dates for indexing
    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));

    const parseNum = (val: any) => parseFloat(val?.toString() || "0");

    return data.map((item) => {
      const formattedData = item.formatted_data || [];
      const eveningData =
        formattedData.find((fd: any) => fd.time_period === "Evening") || {};
      const morningData =
        formattedData.find((fd: any) => fd.time_period === "Morning") || {};

      return {
        date: item.date,
        discom: item.discom,
        district: item.district,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
        avg_daily_load_mw: parseNum(item.avg_daily_load_mw),
        daily_peak_load_mw: parseNum(item.daily_peak_load_mw),

        // Evening peak values
        evening_peak_load_mw: parseNum(eveningData.max_load),
        evening_peak_load_slot: eveningData.max_load_slot,
        evening_min_load_mw: parseNum(eveningData.min_load),
        evening_min_load_slot: eveningData.min_load_slot,
        evening_avg_load_mw: parseNum(eveningData.avg_load),

        // Morning peak values
        morning_peak_load_mw: parseNum(morningData.max_load),
        morning_peak_load_slot: morningData.max_load_slot,
        morning_min_load_mw: parseNum(morningData.min_load),
        morning_min_load_slot: morningData.min_load_slot,
        morning_avg_load_mw: parseNum(morningData.avg_load),

        formatted_data: [
          {
            time_period: "Evening",
            max_load: parseNum(eveningData.max_load),
            min_load: parseNum(eveningData.min_load),
            avg_load: parseNum(eveningData.avg_load),
            max_load_slot: eveningData.max_load_slot,
            min_load_slot: eveningData.min_load_slot,
          },
          {
            time_period: "Morning",
            max_load: parseNum(morningData.max_load),
            min_load: parseNum(morningData.min_load),
            avg_load: parseNum(morningData.avg_load),
            max_load_slot: morningData.max_load_slot,
            min_load_slot: morningData.min_load_slot,
          },
        ],
      };
    });
  } catch (error) {
    console.error("Error fetching GSS Load Daily Discom District data:", error);
    return [];
  }
}
export async function getRestrictedGssLoadDailyTotal(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-daily-total?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = toArray<any>(res);
    if (!data || data.length === 0) return [];

    // Get unique dates for indexing
    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));

    const parseNum = (val: any) => parseFloat(val?.toString() || "0");

    return data.map((item) => {
      const formattedData = item.formatted_data || [];
      const eveningData =
        formattedData.find((fd: any) => fd.time_period === "Evening") || {};
      const morningData =
        formattedData.find((fd: any) => fd.time_period === "Morning") || {};

      return {
        date: item.date,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
        avg_daily_load_mw: parseNum(item.avg_daily_load_mw),
        daily_peak_load_mw: parseNum(item.daily_peak_load_mw),

        // evening  peak values
        evening_peak_load_mw: parseNum(eveningData.max_load),
        evening_peak_load_slot: eveningData.max_load_slot,
        evening_min_load_mw: parseNum(eveningData.min_load),
        evening_min_load_slot: eveningData.min_load_slot,
        evening_avg_load_mw: parseNum(eveningData.avg_load),

        // Moring peak values
        morning_peak_load_mw: parseNum(morningData.max_load),
        morning_peak_load_slot: morningData.max_load_slot,
        morning_min_load_mw: parseNum(morningData.min_load),
        morning_min_load_slot: morningData.min_load_slot,
        morning_avg_load_mw: parseNum(morningData.avg_load),

        formatted_data: [
          {
            time_period: "Evening",
            max_load: parseNum(eveningData.max_load),
            min_load: parseNum(eveningData.min_load),
            avg_load: parseNum(eveningData.avg_load),
            max_load_slot: eveningData.max_load_slot,
            min_load_slot: eveningData.min_load_slot,
          },
          {
            time_period: "Morning",
            max_load: parseNum(morningData.max_load),
            min_load: parseNum(morningData.min_load),
            avg_load: parseNum(morningData.avg_load),
            max_load_slot: morningData.max_load_slot,
            min_load_slot: morningData.min_load_slot,
          },
        ],
      };
    });
  } catch (error) {
    console.error("[DAILY TOTAL API] Error fetching data:", error);
    return [];
  }
}

export async function getRestrictedGssLoadMonthlyTotal(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-monthly-total?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = toArray<any>(res);
    if (!data || data.length === 0) return [];

    // Helper function to format month
    const getMonthWithYear = (month: number, year: number): string => {
      if (!month || !year) return "";
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const yearShort = year.toString().slice(-2);
      return `${monthNames[month - 1] || ""}-${yearShort}`;
    };

    const parseNum = (val: any) => parseFloat(val?.toString() || "0");

    return data.map((item) => {
      return {
        month: getMonthWithYear(item.month, item.year),
        year: item.year,
        dates: `${startDate} to ${endDate}`,

        // Rename fields to match daily data structure
        avg_daily_load_mw: parseNum(item.average_load_mw),
        daily_peak_load_mw: parseNum(item.peak_load_mw),
        date_of_peak_load: item.date_of_peak_load,
        timeblock_of_peak_load: item.timeblock_of_peak_load,

        // Morning peak load
        morning_peak_load_mw: parseNum(item.morning_peak_load_mw),
        date_of_morning_peak_load: item.date_of_morning_peak_load,
        timeblock_of_morning_peak_load: item.timeblock_of_morning_peak_load,

        // Evening peak load
        evening_peak_load_mw: parseNum(item.evening_peak_load_mw),
        date_of_evening_peak_load: item.date_of_evening_peak_load,
        timeblock_of_evening_peak_load: item.timeblock_of_evening_peak_load,
      };
    });
  } catch (error) {
    console.error("[MONTHLY TOTAL API] Error fetching data:", error);
    return [];
  }
}
export async function getrestrictedAreaGssLoadDaily(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-daily?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = toArray<any>(res);
    if (!data || data.length === 0) return [];

    // Get unique dates for indexing
    const uniqueDates = Array.from(new Set(data.map((item) => item.date)));

    return data.map((item) => {
      const formattedData = item.formatted_data || [];
      const eveningData =
        formattedData.find((fd: any) => fd.time_period === "Evening") || {};
      const morningData =
        formattedData.find((fd: any) => fd.time_period === "Morning") || {};

      const parseNum = (val: any) => parseFloat(val?.toString() || "0");

      return {
        date: item.date,
        discom: item.discom,
        divisional_headquarter: item.divisional_headquarter,
        district: item.district,
        area: item.station,
        station: item.station,
        dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
        dates: `${startDate} to ${endDate}`,
        month: getMonthFromDate(item.date),
        avg_daily_load_mw: parseNum(item.avg_daily_load_mw),
        daily_peak_load_mw: parseNum(item.daily_peak_load_mw),

        // Evening peak values
        evening_peak_load_mw: parseNum(eveningData.max_load),
        evening_peak_load_slot: eveningData.max_load_slot,
        evening_min_load_mw: parseNum(eveningData.min_load),
        evening_min_load_slot: eveningData.min_load_slot,
        evening_avg_load_mw: parseNum(eveningData.avg_load),

        // Morning peak values
        morning_peak_load_mw: parseNum(morningData.max_load),
        morning_peak_load_slot: morningData.max_load_slot,
        morning_min_load_mw: parseNum(morningData.min_load),
        morning_min_load_slot: morningData.min_load_slot,
        morning_avg_load_mw: parseNum(morningData.avg_load),

        formatted_data: [
          {
            time_period: "Evening",
            max_load: parseNum(eveningData.max_load),
            min_load: parseNum(eveningData.min_load),
            avg_load: parseNum(eveningData.avg_load),
            max_load_slot: eveningData.max_load_slot,
            min_load_slot: eveningData.min_load_slot,
          },
          {
            time_period: "Morning",
            max_load: parseNum(morningData.max_load),
            min_load: parseNum(morningData.min_load),
            avg_load: parseNum(morningData.avg_load),
            max_load_slot: morningData.max_load_slot,
            min_load_slot: morningData.min_load_slot,
          },
        ],
      };
    });
  } catch (error) {
    console.error("Error fetching GSS Load Daily data:", error);
    return [];
  }
}
export async function getGssrestrictedAreaLoadMonthly(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-monthly?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = toArray<any>(res);
    if (!data || data.length === 0) return [];

    // Helper function to format month
    const getMonthWithYear = (month: number, year: number): string => {
      if (!month || !year) return "";
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const yearShort = year.toString().slice(-2);
      return `${monthNames[month - 1] || ""}-${yearShort}`;
    };

    const parseNum = (val: any) => parseFloat(val?.toString() || "0");

    return data.map((item) => {
      return {
        // Add the formatted month field that the component expects
        month: getMonthWithYear(item.month, item.year),
        year: item.year,
        discom: item.discom,
        divisional_headquarter: item.divisional_headquarter,
        district: item.district,
        area: item.station,
        station: item.station,
        dates: `${startDate} to ${endDate}`,

        // Rename fields to match daily data structure
        avg_daily_load_mw: parseNum(item.average_load_mw),
        daily_peak_load_mw: parseNum(item.peak_load_mw),
        date_of_peak_load: item.date_of_peak_load,
        timeblock_of_peak_load: item.timeblock_of_peak_load,

        // Morning peak load
        morning_peak_load_mw: parseNum(item.morning_peak_load_mw),
        date_of_morning_peak_load: item.date_of_morning_peak_load,
        timeblock_of_morning_peak_load: item.timeblock_of_morning_peak_load,

        // Evening peak load
        evening_peak_load_mw: parseNum(item.evening_peak_load_mw),
        date_of_evening_peak_load: item.date_of_evening_peak_load,
        timeblock_of_evening_peak_load: item.timeblock_of_evening_peak_load,
      };
    });
  } catch (error) {
    console.error("Error fetching GSS Load Monthly data:", error);
    return [];
  }
}

export async function getMonthlyRestrictedDistrictDiscomGssLoad(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const res = await buildHttpResponse({
      endPoint: `rajasthan/gss-load-monthly-discom-district?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = toArray<any>(res);
    if (!data || data.length === 0) return [];

    // Helper function to format month
    const getMonthWithYear = (month: number, year: number): string => {
      if (!month || !year) return "";
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const yearShort = year.toString().slice(-2);
      return `${monthNames[month - 1] || ""}-${yearShort}`;
    };

    const parseNum = (val: any) => parseFloat(val?.toString() || "0");

    return data.map((item: any) => {
      return {
        month: getMonthWithYear(item.month, item.year),
        year: item.year,
        discom: item.discom,
        district: item.district,

        // Average load
        average_load_mw: parseNum(item.average_load_mw),
        avg_daily_load_mw: parseNum(item.average_load_mw),

        // Overall peak load
        peak_load_mw: parseNum(item.peak_load_mw),
        daily_peak_load_mw: parseNum(item.peak_load_mw),
        date_of_peak_load: item.date_of_peak_load,
        timeblock_of_peak_load: item.timeblock_of_peak_load,

        // Morning peak load
        morning_peak_load_mw: parseNum(item.morning_peak_load_mw),
        date_of_morning_peak_load: item.date_of_morning_peak_load,
        timeblock_of_morning_peak_load: item.timeblock_of_morning_peak_load,

        // Evening peak load
        evening_peak_load_mw: parseNum(item.evening_peak_load_mw),
        date_of_evening_peak_load: item.date_of_evening_peak_load,
        timeblock_of_evening_peak_load: item.timeblock_of_evening_peak_load,
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Monthly Discom District GSS Load data:",
      error,
    );
    return [];
  }
}
export async function getMonthlyDiscomDrawalSchedule(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{ data: any[] }> = await buildHttpResponse({
      endPoint: `rajasthan/getMonthlyDiscomDrawalSchedule?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    return data.map((item: any) => {
      return {
        month: item.month,
        year: item.year,
        discom: item.discom,
        average_drawal_mw: parseFloat(
          item.average_drawal_mw?.toString() || "0",
        ),
        average_schedule_mw: parseFloat(
          item.average_schedule_mw?.toString() || "0",
        ),

        // Peak drawal
        peak_drawal_mw: parseFloat(item.peak_drawal_mw?.toString() || "0"),
        date_of_peak_drawal: item.date_of_peak_drawal,
        timeblock_of_peak_drawal: item.timeblock_of_peak_drawal,

        // Peak schedule
        peak_schedule_mw: parseFloat(item.peak_schedule_mw?.toString() || "0"),
        date_of_peak_schedule: item.date_of_peak_schedule,
        timeblock_of_peak_schedule: item.timeblock_of_peak_schedule,

        // Morning peak drawal
        morning_peak_drawal_mw: parseFloat(
          item.morning_peak_drawal_mw?.toString() || "0",
        ),
        date_of_morning_peak_drawal: item.date_of_morning_peak_drawal,
        timeblock_of_morning_peak_drawal: item.timeblock_of_morning_peak_drawal,

        // Morning peak schedule
        morning_peak_schedule_mw: parseFloat(
          item.morning_peak_schedule_mw?.toString() || "0",
        ),
        date_of_morning_peak_schedule: item.date_of_morning_peak_schedule,
        timeblock_of_morning_peak_schedule:
          item.timeblock_of_morning_peak_schedule,

        // Evening peak drawal
        evening_peak_drawal_mw: parseFloat(
          item.evening_peak_drawal_mw?.toString() || "0",
        ),
        date_of_evening_peak_drawal: item.date_of_evening_peak_drawal,
        timeblock_of_evening_peak_drawal: item.timeblock_of_evening_peak_drawal,

        // Evening peak schedule
        evening_peak_schedule_mw: parseFloat(
          item.evening_peak_schedule_mw?.toString() || "0",
        ),
        date_of_evening_peak_schedule: item.date_of_evening_peak_schedule,
        timeblock_of_evening_peak_schedule:
          item.timeblock_of_evening_peak_schedule,
      };
    });
  } catch (error) {
    console.error("Error fetching Monthly Discom Drawal Schedule data:", error);
    return [];
  }
}

// Monthly Peak API
export async function getChhattisgarhMonthlyPeakDiscom(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `chhattisgarh/getMonthlyPeakDemand?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = Array.isArray(response?.data) ? response.data : [];
    if (data.length === 0) return [];

    return data.map((item: any) => {
      return {
        month: item.month,
        year: item.year,
        average_mw: parseFloat(item.average_mw?.toString() ?? "0"),
        peak_mw: parseFloat(item.peak_mw?.toString() ?? "0"),
        date_of_peak: item.date_of_peak,
        timeblock_of_peak: item.timeblock_of_peak,
        morning_peak_mw: parseFloat(item.morning_peak_mw?.toString() ?? "0"),
        date_of_morning_peak: item.date_of_morning_peak,
        timeblock_of_morning_peak: item.timeblock_of_morning_peak,
        evening_peak_mw: parseFloat(item.evening_peak_mw?.toString() ?? "0"),
        date_of_evening_peak: item.date_of_evening_peak,
        timeblock_of_evening_peak: item.timeblock_of_evening_peak,
      };
    });
  } catch (error) {
    console.error("Error fetching Chhattisgarh Monthly Peak data:", error);
    return [];
  }
}

export async function getChhattisgarhMonthlyPeakForecast(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `chhattisgarh/getMonthlyPeakForecast?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = Array.isArray(response?.data) ? response.data : [];
    if (data.length === 0) return [];

    // Group by month-year, separate T+1 and T+2
    const byMonthYear = new Map<string, { d1?: any; d2?: any }>();
    for (const item of data) {
      const key = `${item.month}-${item.year}`;
      const entry = byMonthYear.get(key) ?? {};
      if (item.forecast_type === "T+1") entry.d1 = item;
      else if (item.forecast_type === "T+2") entry.d2 = item;
      byMonthYear.set(key, entry);
    }

    return Array.from(byMonthYear.values()).map(({ d1, d2 }) => {
      const base = d1 ?? d2;
      return {
        month: base.month,
        year: base.year,
        // T+1 fields
        average_mw: parseFloat(d1?.average_mw?.toString() ?? "0"),
        peak_mw: parseFloat(d1?.peak_mw?.toString() ?? "0"),
        date_of_peak: d1?.date_of_peak,
        timeblock_of_peak: d1?.timeblock_of_peak,
        morning_peak_mw: parseFloat(d1?.morning_peak_mw?.toString() ?? "0"),
        date_of_morning_peak: d1?.date_of_morning_peak,
        timeblock_of_morning_peak: d1?.timeblock_of_morning_peak,
        evening_peak_mw: parseFloat(d1?.evening_peak_mw?.toString() ?? "0"),
        date_of_evening_peak: d1?.date_of_evening_peak,
        timeblock_of_evening_peak: d1?.timeblock_of_evening_peak,
        // T+2 fields
        d2_average_mw: parseFloat(d2?.average_mw?.toString() ?? "0"),
        d2_peak_mw: parseFloat(d2?.peak_mw?.toString() ?? "0"),
        d2_timeblock_of_peak: d2?.timeblock_of_peak ?? "",
        d2_morning_peak_mw: parseFloat(d2?.morning_peak_mw?.toString() ?? "0"),
        d2_timeblock_of_morning_peak: d2?.timeblock_of_morning_peak ?? "",
        d2_evening_peak_mw: parseFloat(d2?.evening_peak_mw?.toString() ?? "0"),
        d2_timeblock_of_evening_peak: d2?.timeblock_of_evening_peak ?? "",
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Chhattisgarh Monthly Peak Forecast data:",
      error,
    );
    return [];
  }
}

export async function getDailyTotalDrawalSchedule(
  startDate: string,
  endDate: string,
  sourcesParam: string = "",
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getDailyTotalDrawalSchedule?start_date=${startDate}&end_date=${endDate}${sourcesParam}`,
      method: HttpMethod.GET,
    });

    // ✅ Unwrap { success, count, data } envelope
    return Array.isArray(response) ? response : (response?.data ?? []);
  } catch (error) {
    console.error("Error fetching Daily Total Drawal Schedule data:", error);
    return [];
  }
}
export async function getMonthlyTotalDrawalSchedule(
  startDate: string,
  endDate: string,
  sourcesParam: string = "",
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getMonthlyTotalDrawalSchedule?start_date=${startDate}&end_date=${endDate}${sourcesParam}`,
      method: HttpMethod.GET,
    });

    // ✅ Unwrap { success, count, data } envelope
    return Array.isArray(response) ? response : (response?.data ?? []);
  } catch (error) {
    console.error("Error fetching Monthly Total Drawal Schedule data:", error);
    return [];
  }
}
export async function getTotalDrawalScheduleMetrics(
  startDate: string,
  endDate: string,
  sourcesParam: string = "",
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getTotalDrawalScheduleMetrics?start_date=${startDate}&end_date=${endDate}${sourcesParam}`,
      method: HttpMethod.GET,
    });

    // ✅ Unwrap { success, data } envelope
    return Array.isArray(response) ? response : (response?.data ?? []);
  } catch (error) {
    console.error("Error fetching Total Drawal Schedule Metrics data:", error);
    return [];
  }
}

export async function getRajasthanDrawalScheduleForecastMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getDrawalScheduleForecastMetrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = await response;

    // Transform the data to match your application's structure
    return data.map((item: any) => ({
      ...item,
      // Ensure consistent naming
      block: item.block,
      discom: item.discom,
      model: item.model,
      forecast_type: item.forecast_type, // "T+1" or "T+2"
      time_string: item.time_string,

      // Schedule metrics
      min_schedule: item.min_schedule || 0,
      max_schedule: item.max_schedule || 0,
      avg_schedule: item.avg_schedule || 0,
      mode_schedule_window_formula: item.mode_schedule_window_formula || 0,
      date_of_min_schedule: item.date_of_min_schedule,
      date_of_max_schedule: item.date_of_max_schedule,

      // Drawal metrics
      min_drawal: item.min_drawal || 0,
      max_drawal: item.max_drawal || 0,
      avg_drawal: item.avg_drawal || 0,
      mode_drawal_window_formula: item.mode_drawal_window_formula || 0,
      date_of_min_drawal: item.date_of_min_drawal,
      date_of_max_drawal: item.date_of_max_drawal,
    }));
  } catch (error) {
    console.error("Error fetching Rajasthan forecast metrics:", error);
    return [];
  }
}

export async function getRajasthanDailyPeakForecast(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getDailyDrawalScheduleForecast?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = await response;

    // Return the data array, filtering for Total discom only
    if (data && Array.isArray(data.data)) {
      return data.data
        .filter((item: any) => item.discom === "Total")
        .map((item: any) => ({
          date: item.date,
          discom: item.discom,
          model: item.model,
          forecast_type: item.forecast_type, // "T+1" or "T+2"
          avg_daily_schedule_mw: item.avg_daily_schedule_mw || 0,
          daily_peak_schedule_mw: item.daily_peak_schedule_mw || 0,
          avg_daily_drawal_mw: item.avg_daily_drawal_mw || 0,
          daily_peak_drawal_mw: item.daily_peak_drawal_mw || 0,
          formatted_data: item.formatted_data || [],
        }));
    }

    return [];
  } catch (error) {
    console.error("Error fetching daily peak forecast:", error);
    return [];
  }
}

export async function getRajasthanMonthlyPeakForecast(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/getMonthlyDrawalScheduleForecast?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const data = await response;

    // Return the data array, filtering for Total discom only
    if (data && Array.isArray(data.data)) {
      return data.data
        .filter((item: any) => item.discom === "Total")
        .map((item: any) => ({
          month: item.month,
          year: item.year,
          discom: item.discom,
          model: item.model,
          forecast_type: item.forecast_type, // "T+1" or "T+2"
          average_schedule_mw: item.average_schedule_mw || 0,
          peak_schedule_mw: item.peak_schedule_mw || 0,
          date_of_peak_schedule: item.date_of_peak_schedule,
          block_of_peak_schedule: item.block_of_peak_schedule,
          morning_peak_schedule_mw: item.morning_peak_schedule_mw || 0,
          date_of_morning_peak_schedule: item.date_of_morning_peak_schedule,
          block_of_morning_peak_schedule: item.block_of_morning_peak_schedule,
          evening_peak_schedule_mw: item.evening_peak_schedule_mw || 0,
          date_of_evening_peak_schedule: item.date_of_evening_peak_schedule,
          block_of_evening_peak_schedule: item.block_of_evening_peak_schedule,
          average_drawal_mw: item.average_drawal_mw || 0,
          peak_drawal_mw: item.peak_drawal_mw || 0,
          date_of_peak_drawal: item.date_of_peak_drawal,
          block_of_peak_drawal: item.block_of_peak_drawal,
          morning_peak_drawal_mw: item.morning_peak_drawal_mw || 0,
          date_of_morning_peak_drawal: item.date_of_morning_peak_drawal,
          block_of_morning_peak_drawal: item.block_of_morning_peak_drawal,
          evening_peak_drawal_mw: item.evening_peak_drawal_mw || 0,
          date_of_evening_peak_drawal: item.date_of_evening_peak_drawal,
          block_of_evening_peak_drawal: item.block_of_evening_peak_drawal,
        }));
    }

    return [];
  } catch (error) {
    console.error("Error fetching monthly peak forecast:", error);
    return [];
  }
}

export async function getRajasthanSurplusDeficitMetrics(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `rajasthan/surplus-deficit-metrics?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    // API returns { success, count, data: [...] }
    const res = response?.data;

    if (!Array.isArray(res) || res.length === 0) {
      console.warn("[SURPLUS-DEFICIT API] No data returned");
      return [];
    }

    return res.map((item: any) => ({
      slot: item.time_block,

      // Drawal metrics
      min_drawal: parseFloat(item.min_drawal ?? 0),
      date_of_min_drawal: item.date_of_min_drawal,
      max_drawal: parseFloat(item.max_drawal ?? 0),
      date_of_max_drawal: item.date_of_max_drawal,
      avg_drawal: parseFloat(item.avg_drawal ?? 0),
      mode_drawal: parseFloat(item.mode_drawal ?? 0),

      // Total Generation + Procurement metrics
      min_total_gen_proc: parseFloat(item.min_total_gen_proc ?? 0),
      date_of_min_total_gen_proc: item.date_of_min_total_gen_proc,
      max_total_gen_proc: parseFloat(item.max_total_gen_proc ?? 0),
      date_of_max_total_gen_proc: item.date_of_max_total_gen_proc,
      avg_total_gen_proc: parseFloat(item.avg_total_gen_proc ?? 0),
      mode_total_gen_proc: parseFloat(item.mode_total_gen_proc ?? 0),

      // Surplus / Deficit metrics
      min_surplus_deficit: parseFloat(item.min_surplus_deficit ?? 0),
      date_of_min_surplus_deficit: item.date_of_min_surplus_deficit,
      max_surplus_deficit: parseFloat(item.max_surplus_deficit ?? 0),
      date_of_max_surplus_deficit: item.date_of_max_surplus_deficit,
      avg_surplus_deficit: parseFloat(item.avg_surplus_deficit ?? 0),
      mode_surplus_deficit: parseFloat(item.mode_surplus_deficit ?? 0),

      // Surplus / Deficit percentage metrics
      min_surplus_deficit_pct: parseFloat(item.min_surplus_deficit_pct ?? 0),
      date_of_min_surplus_deficit_pct: item.date_of_min_surplus_deficit_pct,
      max_surplus_deficit_pct: parseFloat(item.max_surplus_deficit_pct ?? 0),
      date_of_max_surplus_deficit_pct: item.date_of_max_surplus_deficit_pct,
      avg_surplus_deficit_pct: parseFloat(item.avg_surplus_deficit_pct ?? 0),
      mode_surplus_deficit_pct: parseFloat(item.mode_surplus_deficit_pct ?? 0),
    }));
  } catch (error) {
    console.error("[SURPLUS-DEFICIT API] Error fetching data:", error);
    if (error instanceof Error) {
      console.error("[SURPLUS-DEFICIT API] Message:", error.message);
      console.error("[SURPLUS-DEFICIT API] Stack:", error.stack);
    }
    return [];
  }
}
export async function getRajasthanMonthlySurplusDeficit(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{ data: any[] }> = await buildHttpResponse({
      endPoint: `rajasthan/surplus-deficit-monthly?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    return data.map((item: any) => {
      return {
        year: item.year,
        month: item.month,

        // Drawal metrics
        average_drawal_mw: parseFloat(
          item.average_drawal_mw?.toString() || "0",
        ),
        peak_drawal_mw: parseFloat(item.peak_drawal_mw?.toString() || "0"),
        date_of_peak_drawal: item.date_of_peak_drawal,
        block_of_peak_drawal: item.block_of_peak_drawal,
        morning_peak_drawal_mw: parseFloat(
          item.morning_peak_drawal_mw?.toString() || "0",
        ),
        date_of_morning_peak_drawal: item.date_of_morning_peak_drawal,
        block_of_morning_peak_drawal: item.block_of_morning_peak_drawal,
        evening_peak_drawal_mw: parseFloat(
          item.evening_peak_drawal_mw?.toString() || "0",
        ),
        date_of_evening_peak_drawal: item.date_of_evening_peak_drawal,
        block_of_evening_peak_drawal: item.block_of_evening_peak_drawal,

        // Generation/Procurement metrics
        average_total_gen_proc_mw: parseFloat(
          item.average_total_gen_proc_mw?.toString() || "0",
        ),
        peak_total_gen_proc_mw: parseFloat(
          item.peak_total_gen_proc_mw?.toString() || "0",
        ),
        date_of_peak_total_gen_proc: item.date_of_peak_total_gen_proc,
        block_of_peak_total_gen_proc: item.block_of_peak_total_gen_proc,
        morning_peak_total_gen_proc_mw: parseFloat(
          item.morning_peak_total_gen_proc_mw?.toString() || "0",
        ),
        date_of_morning_peak_total_gen_proc:
          item.date_of_morning_peak_total_gen_proc,
        block_of_morning_peak_total_gen_proc:
          item.block_of_morning_peak_total_gen_proc,
        evening_peak_total_gen_proc_mw: parseFloat(
          item.evening_peak_total_gen_proc_mw?.toString() || "0",
        ),
        date_of_evening_peak_total_gen_proc:
          item.date_of_evening_peak_total_gen_proc,
        block_of_evening_peak_total_gen_proc:
          item.block_of_evening_peak_total_gen_proc,

        // Surplus/Deficit metrics
        average_surplus_deficit_mw: parseFloat(
          item.average_surplus_deficit_mw?.toString() || "0",
        ),
        peak_surplus_deficit_mw: parseFloat(
          item.peak_surplus_deficit_mw?.toString() || "0",
        ),
        date_of_peak_surplus_deficit: item.date_of_peak_surplus_deficit,
        block_of_peak_surplus_deficit: item.block_of_peak_surplus_deficit,
        morning_peak_surplus_deficit_mw: parseFloat(
          item.morning_peak_surplus_deficit_mw?.toString() || "0",
        ),
        date_of_morning_peak_surplus_deficit:
          item.date_of_morning_peak_surplus_deficit,
        block_of_morning_peak_surplus_deficit:
          item.block_of_morning_peak_surplus_deficit,
        evening_peak_surplus_deficit_mw: parseFloat(
          item.evening_peak_surplus_deficit_mw?.toString() || "0",
        ),
        date_of_evening_peak_surplus_deficit:
          item.date_of_evening_peak_surplus_deficit,
        block_of_evening_peak_surplus_deficit:
          item.block_of_evening_peak_surplus_deficit,

        // Percentage metrics
        average_surplus_deficit_pct: parseFloat(
          item.average_surplus_deficit_pct?.toString() || "0",
        ),
        peak_surplus_deficit_pct: parseFloat(
          item.peak_surplus_deficit_pct?.toString() || "0",
        ),
        date_of_peak_surplus_deficit_pct: item.date_of_peak_surplus_deficit_pct,
        block_of_peak_surplus_deficit_pct:
          item.block_of_peak_surplus_deficit_pct,
        morning_peak_surplus_deficit_pct: parseFloat(
          item.morning_peak_surplus_deficit_pct?.toString() || "0",
        ),
        date_of_morning_peak_surplus_deficit_pct:
          item.date_of_morning_peak_surplus_deficit_pct,
        block_of_morning_peak_surplus_deficit_pct:
          item.block_of_morning_peak_surplus_deficit_pct,
        evening_peak_surplus_deficit_pct: parseFloat(
          item.evening_peak_surplus_deficit_pct?.toString() || "0",
        ),
        date_of_evening_peak_surplus_deficit_pct:
          item.date_of_evening_peak_surplus_deficit_pct,
        block_of_evening_peak_surplus_deficit_pct:
          item.block_of_evening_peak_surplus_deficit_pct,
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Rajasthan Monthly Surplus/Deficit data:",
      error,
    );
    return [];
  }
}

export async function getRajasthanDailySurplusDeficit(
  startDate: string,
  endDate: string,
): Promise<any[]> {
  try {
    const response: Promise<{ data: any[] }> = await buildHttpResponse({
      endPoint: `rajasthan/surplus-deficit-daily?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
    });

    const res = await response;
    const data = res.data;

    return data.map((item: any) => {
      return {
        date: item.date,

        // Daily average metrics
        avg_daily_drawal_mw: parseFloat(
          item.avg_daily_drawal_mw?.toString() || "0",
        ),
        daily_peak_drawal_mw: parseFloat(
          item.daily_peak_drawal_mw?.toString() || "0",
        ),
        avg_daily_total_gen_proc_mw: parseFloat(
          item.avg_daily_total_gen_proc_mw?.toString() || "0",
        ),
        daily_peak_total_gen_proc_mw: parseFloat(
          item.daily_peak_total_gen_proc_mw?.toString() || "0",
        ),
        avg_daily_surplus_deficit_mw: parseFloat(
          item.avg_daily_surplus_deficit_mw?.toString() || "0",
        ),
        daily_peak_surplus_deficit_mw: parseFloat(
          item.daily_peak_surplus_deficit_mw?.toString() || "0",
        ),
        avg_daily_surplus_deficit_pct: parseFloat(
          item.avg_daily_surplus_deficit_pct?.toString() || "0",
        ),
        daily_peak_surplus_deficit_pct: parseFloat(
          item.daily_peak_surplus_deficit_pct?.toString() || "0",
        ),

        // Formatted data by time period (Morning/Evening)
        formatted_data:
          item.formatted_data?.map((period: any) => ({
            time_period: period.time_period,

            // Drawal metrics
            avg_drawal: parseFloat(period.avg_drawal?.toString() || "0"),
            max_drawal: parseFloat(period.max_drawal?.toString() || "0"),
            min_drawal: parseFloat(period.min_drawal?.toString() || "0"),
            max_drawal_block: period.max_drawal_block,
            min_drawal_block: period.min_drawal_block,

            // Generation/Procurement metrics
            avg_total_gen_proc: parseFloat(
              period.avg_total_gen_proc?.toString() || "0",
            ),
            max_total_gen_proc: parseFloat(
              period.max_total_gen_proc?.toString() || "0",
            ),
            min_total_gen_proc: parseFloat(
              period.min_total_gen_proc?.toString() || "0",
            ),
            max_total_gen_proc_block: period.max_total_gen_proc_block,
            min_total_gen_proc_block: period.min_total_gen_proc_block,

            // Surplus/Deficit metrics
            avg_surplus_deficit: parseFloat(
              period.avg_surplus_deficit?.toString() || "0",
            ),
            max_surplus_deficit: parseFloat(
              period.max_surplus_deficit?.toString() || "0",
            ),
            min_surplus_deficit: parseFloat(
              period.min_surplus_deficit?.toString() || "0",
            ),
            max_surplus_deficit_block: period.max_surplus_deficit_block,
            min_surplus_deficit_block: period.min_surplus_deficit_block,

            // Percentage metrics
            avg_surplus_deficit_pct: parseFloat(
              period.avg_surplus_deficit_pct?.toString() || "0",
            ),
            max_surplus_deficit_pct: parseFloat(
              period.max_surplus_deficit_pct?.toString() || "0",
            ),
            min_surplus_deficit_pct: parseFloat(
              period.min_surplus_deficit_pct?.toString() || "0",
            ),
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Rajasthan Daily Surplus/Deficit data:",
      error,
    );
    return [];
  }
}

// Procurement Optimizer Session

export async function fetchProcurementRuvnlSession(
  startDate: string,
  endDate: string,
) {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/start-session?start_date=${startDate}&end_date=${endDate}`,
    method: HttpMethod.POST,
    body: {
      start_date: startDate,
      end_date: endDate,
    },
  });
  return response;
}

export async function getProcurementRuvnlSession() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/get-session`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function getStatusRuvnlTraderOffersData() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/get-status-power`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function StartSimulationRuvnlProcurementData(
  TraderOffersData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/start-simulation`,
    method: HttpMethod.POST,
    noCache: true,
    body: TraderOffersData,
  });

  return response as ApiResponse;
}
export async function UpdateTraderRuvnlOffersData(
  TraderOffersData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/update-trader-offers`,
    method: HttpMethod.POST,
    noCache: true,
    body: TraderOffersData,
  });

  return response as ApiResponse;
}

export async function fetchTraderRuvnlOffersData() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/get-trader-offers`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function AddTraderRuvnlOffersData(
  TraderOffersData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/add-trader-offers`,
    method: HttpMethod.POST,
    noCache: true,
    body: TraderOffersData,
  });

  return response as ApiResponse;
}

export async function AddSurplusDeficitRuvnlData(
  SurplusDeficitData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/upload-deficit-data`,
    method: HttpMethod.POST,
    noCache: true,
    body: SurplusDeficitData,
  });

  return response as ApiResponse;
}
export async function fetchProcurementRuvnlSimulationMetricsData() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/all-metrics`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function UpdateTraderRuvnlOffersToggleData(
  TraderOffersData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/toggle-trader-offers`,
    method: HttpMethod.POST,
    noCache: true,
    body: TraderOffersData,
  });

  return response as ApiResponse;
}
export async function getStatusTraderRuvnlOffersData() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/get-status-power`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchOptimalTraderRuvnlOffersData() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/get-optimal-trader-offers`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchSimulationRuvnlProcurementData() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/simulation-data`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchProcurementRuvnlCummulativeData() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/simulation-data-cummulative`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchOptimalProcurementRuvnlData() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/optimal-data`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchOptimalProcurementRuvnlCummulativeData() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/optimal-data-cummulative`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchProcurementRuvnlStatistics() {
  const response = await buildHttpResponse({
    endPoint: `rajasthan/procurement/summary-metrics`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function getGenerationEltrixBackdownDailySourcePlant(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  plantNames?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add generation sources if provided
    if (generationSources && generationSources.length > 0) {
      queryParams += `&generation_source=${generationSources.join(",")}`;
    }

    // Add plant names if provided
    if (plantNames && plantNames.length > 0) {
      queryParams += `&plant_name=${plantNames.join(",")}`;
    }

    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/eltrix-backdown-daily-source-plant?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const res = await response;

    if (!res.success) {
      console.error("API returned unsuccessful response:", res.message);
      return [];
    }

    const data = res.data;

    return data.map((item: any) => {
      return {
        date: item.date,
        generation_source: item.generation_source,
        total_plants: item.total_plants,

        // Source-level daily metrics
        source_avg_daily_onbards_mw: parseFloat(
          item.source_avg_daily_onbards_mw?.toString() || "0",
        ),
        source_peak_daily_onbards_mw: parseFloat(
          item.source_peak_daily_onbards_mw?.toString() || "0",
        ),
        source_avg_daily_onbardc_mw: parseFloat(
          item.source_avg_daily_onbardc_mw?.toString() || "0",
        ),
        source_peak_daily_onbardc_mw: parseFloat(
          item.source_peak_daily_onbardc_mw?.toString() || "0",
        ),
        source_avg_daily_backdown_mw: parseFloat(
          item.source_avg_daily_backdown_mw?.toString() || "0",
        ),
        source_peak_daily_backdown_mw: parseFloat(
          item.source_peak_daily_backdown_mw?.toString() || "0",
        ),

        // Plant-level data
        plants:
          item.plants?.map((plant: any) => ({
            date: plant.date,
            generation_source: plant.generation_source,
            plant_name: plant.plant_name,

            // Plant-level daily metrics
            avg_daily_onbards_mw: parseFloat(
              plant.avg_daily_onbards_mw?.toString() || "0",
            ),
            daily_peak_onbards_mw: parseFloat(
              plant.daily_peak_onbards_mw?.toString() || "0",
            ),
            avg_daily_onbardc_mw: parseFloat(
              plant.avg_daily_onbardc_mw?.toString() || "0",
            ),
            daily_peak_onbardc_mw: parseFloat(
              plant.daily_peak_onbardc_mw?.toString() || "0",
            ),
            avg_daily_backdown_mw: parseFloat(
              plant.avg_daily_backdown_mw?.toString() || "0",
            ),
            daily_peak_backdown_mw: parseFloat(
              plant.daily_peak_backdown_mw?.toString() || "0",
            ),

            // Formatted data by time period (Morning/Evening)
            formatted_data:
              plant.formatted_data?.map((period: any) => ({
                time_period: period.time_period,

                // ONBARDS metrics
                avg_onbards: parseFloat(period.avg_onbards?.toString() || "0"),
                max_onbards: parseFloat(period.max_onbards?.toString() || "0"),
                min_onbards: parseFloat(period.min_onbards?.toString() || "0"),
                max_onbards_slot: period.max_onbards_slot,
                min_onbards_slot: period.min_onbards_slot,

                // ONBARDC metrics
                avg_onbardc: parseFloat(period.avg_onbardc?.toString() || "0"),
                max_onbardc: parseFloat(period.max_onbardc?.toString() || "0"),
                min_onbardc: parseFloat(period.min_onbardc?.toString() || "0"),
                max_onbardc_slot: period.max_onbardc_slot,
                min_onbardc_slot: period.min_onbardc_slot,

                // Backdown metrics
                avg_backdown: parseFloat(
                  period.avg_backdown?.toString() || "0",
                ),
                max_backdown: parseFloat(
                  period.max_backdown?.toString() || "0",
                ),
                min_backdown: parseFloat(
                  period.min_backdown?.toString() || "0",
                ),
                max_backdown_slot: period.max_backdown_slot,
                min_backdown_slot: period.min_backdown_slot,
              })) || [],
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Eltrix Backdown Daily Source/Plant data:",
      error,
    );
    return [];
  }
}
export async function getGenerationEltrixBackdownMonthlySourcePlant(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  plantNames?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add generation sources if provided
    if (generationSources && generationSources.length > 0) {
      queryParams += `&generation_source=${generationSources.join(",")}`;
    }

    // Add plant names if provided
    if (plantNames && plantNames.length > 0) {
      queryParams += `&plant_name=${plantNames.join(",")}`;
    }

    const response: Promise<{
      success: boolean;
      message: string;
      min_date: string;
      max_date: string;
      count: number;
      data: any[];
    }> = await buildHttpResponse({
      endPoint: `rajasthan/eltrix-backdown-monthly-source-plant?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const res = await response;

    if (!res.success) {
      console.error("API returned unsuccessful response:", res.message);
      return [];
    }

    const data = res.data;

    return data.map((item: any) => {
      return {
        year: item.year,
        month: item.month,
        generation_source: item.generation_source,
        total_plants: item.total_plants,

        // Source-level monthly metrics
        source_average_onbards_mw: parseFloat(
          item.source_average_onbards_mw?.toString() || "0",
        ),
        source_peak_onbards_mw: parseFloat(
          item.source_peak_onbards_mw?.toString() || "0",
        ),
        source_average_onbardc_mw: parseFloat(
          item.source_average_onbardc_mw?.toString() || "0",
        ),
        source_peak_onbardc_mw: parseFloat(
          item.source_peak_onbardc_mw?.toString() || "0",
        ),
        source_average_backdown_mw: parseFloat(
          item.source_average_backdown_mw?.toString() || "0",
        ),
        source_peak_backdown_mw: parseFloat(
          item.source_peak_backdown_mw?.toString() || "0",
        ),

        // Plant-level data
        plants:
          item.plants?.map((plant: any) => ({
            year: plant.year,
            month: plant.month,
            generation_source: plant.generation_source,
            plant_name: plant.plant_name,

            // ONBARDS metrics
            average_onbards_mw: parseFloat(
              plant.average_onbards_mw?.toString() || "0",
            ),
            peak_onbards_mw: parseFloat(
              plant.peak_onbards_mw?.toString() || "0",
            ),
            date_of_peak_onbards: plant.date_of_peak_onbards,
            slot_of_peak_onbards: plant.slot_of_peak_onbards,
            morning_peak_onbards_mw: parseFloat(
              plant.morning_peak_onbards_mw?.toString() || "0",
            ),
            date_of_morning_peak_onbards: plant.date_of_morning_peak_onbards,
            slot_of_morning_peak_onbards: plant.slot_of_morning_peak_onbards,
            evening_peak_onbards_mw: parseFloat(
              plant.evening_peak_onbards_mw?.toString() || "0",
            ),
            date_of_evening_peak_onbards: plant.date_of_evening_peak_onbards,
            slot_of_evening_peak_onbards: plant.slot_of_evening_peak_onbards,

            // ONBARDC metrics
            average_onbardc_mw: parseFloat(
              plant.average_onbardc_mw?.toString() || "0",
            ),
            peak_onbardc_mw: parseFloat(
              plant.peak_onbardc_mw?.toString() || "0",
            ),
            date_of_peak_onbardc: plant.date_of_peak_onbardc,
            slot_of_peak_onbardc: plant.slot_of_peak_onbardc,
            morning_peak_onbardc_mw: parseFloat(
              plant.morning_peak_onbardc_mw?.toString() || "0",
            ),
            date_of_morning_peak_onbardc: plant.date_of_morning_peak_onbardc,
            slot_of_morning_peak_onbardc: plant.slot_of_morning_peak_onbardc,
            evening_peak_onbardc_mw: parseFloat(
              plant.evening_peak_onbardc_mw?.toString() || "0",
            ),
            date_of_evening_peak_onbardc: plant.date_of_evening_peak_onbardc,
            slot_of_evening_peak_onbardc: plant.slot_of_evening_peak_onbardc,

            // Backdown metrics
            average_backdown_mw: parseFloat(
              plant.average_backdown_mw?.toString() || "0",
            ),
            peak_backdown_mw: parseFloat(
              plant.peak_backdown_mw?.toString() || "0",
            ),
            date_of_peak_backdown: plant.date_of_peak_backdown,
            slot_of_peak_backdown: plant.slot_of_peak_backdown,
            morning_peak_backdown_mw: parseFloat(
              plant.morning_peak_backdown_mw?.toString() || "0",
            ),
            date_of_morning_peak_backdown: plant.date_of_morning_peak_backdown,
            slot_of_morning_peak_backdown: plant.slot_of_morning_peak_backdown,
            evening_peak_backdown_mw: parseFloat(
              plant.evening_peak_backdown_mw?.toString() || "0",
            ),
            date_of_evening_peak_backdown: plant.date_of_evening_peak_backdown,
            slot_of_evening_peak_backdown: plant.slot_of_evening_peak_backdown,
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Eltrix Backdown Monthly Source/Plant data:",
      error,
    );
    return [];
  }
}

export async function getGenerationEltrixBackdownMetricsSourcePlant(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  plantNames?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add generation sources if provided
    if (generationSources && generationSources.length > 0) {
      queryParams += `&generation_source=${generationSources.join(",")}`;
    }

    // Add plant names if provided
    if (plantNames && plantNames.length > 0) {
      queryParams += `&plant_name=${plantNames.join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/eltrix-backdown-metrics-source-plant?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        slot: item.slot,
        generation_source: item.generation_source,
        total_plants: item.total_plants,

        // Source-level metrics
        source_min_onbards: parseFloat(
          item.source_min_onbards?.toString() || "0",
        ),
        source_date_of_min_onbards: item.source_date_of_min_onbards,
        source_max_onbards: parseFloat(
          item.source_max_onbards?.toString() || "0",
        ),
        source_date_of_max_onbards: item.source_date_of_max_onbards,
        source_avg_onbards: parseFloat(
          item.source_avg_onbards?.toString() || "0",
        ),
        source_mode_onbards: parseFloat(
          item.source_mode_onbards?.toString() || "0",
        ),
        source_min_onbardc: parseFloat(
          item.source_min_onbardc?.toString() || "0",
        ),
        source_date_of_min_onbardc: item.source_date_of_min_onbardc,
        source_max_onbardc: parseFloat(
          item.source_max_onbardc?.toString() || "0",
        ),
        source_date_of_max_onbardc: item.source_date_of_max_onbardc,
        source_avg_onbardc: parseFloat(
          item.source_avg_onbardc?.toString() || "0",
        ),
        source_mode_onbardc: parseFloat(
          item.source_mode_onbardc?.toString() || "0",
        ),
        source_min_backdown: parseFloat(
          item.source_min_backdown?.toString() || "0",
        ),
        source_date_of_min_backdown: item.source_date_of_min_backdown,
        source_max_backdown: parseFloat(
          item.source_max_backdown?.toString() || "0",
        ),
        source_date_of_max_backdown: item.source_date_of_max_backdown,
        source_avg_backdown: parseFloat(
          item.source_avg_backdown?.toString() || "0",
        ),
        source_mode_backdown: parseFloat(
          item.source_mode_backdown?.toString() || "0",
        ),

        // Plant-level data
        plants:
          item.plants?.map((plant: any) => ({
            slot: plant.slot,
            generation_source: plant.generation_source,
            plant_name: plant.plant_name,

            // ONBARDS metrics
            min_onbards: parseFloat(plant.min_onbards?.toString() || "0"),
            date_of_min_onbards: plant.date_of_min_onbards,
            max_onbards: parseFloat(plant.max_onbards?.toString() || "0"),
            date_of_max_onbards: plant.date_of_max_onbards,
            avg_onbards: parseFloat(plant.avg_onbards?.toString() || "0"),
            mode_onbards: parseFloat(plant.mode_onbards?.toString() || "0"),

            // ONBARDC metrics
            min_onbardc: parseFloat(plant.min_onbardc?.toString() || "0"),
            date_of_min_onbardc: plant.date_of_min_onbardc,
            max_onbardc: parseFloat(plant.max_onbardc?.toString() || "0"),
            date_of_max_onbardc: plant.date_of_max_onbardc,
            avg_onbardc: parseFloat(plant.avg_onbardc?.toString() || "0"),
            mode_onbardc: parseFloat(plant.mode_onbardc?.toString() || "0"),

            // Backdown metrics
            min_backdown: parseFloat(plant.min_backdown?.toString() || "0"),
            date_of_min_backdown: plant.date_of_min_backdown,
            max_backdown: parseFloat(plant.max_backdown?.toString() || "0"),
            date_of_max_backdown: plant.date_of_max_backdown,
            avg_backdown: parseFloat(plant.avg_backdown?.toString() || "0"),
            mode_backdown: parseFloat(plant.mode_backdown?.toString() || "0"),
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching Eltrix Backdown Metrics Source/Plant data:",
      error,
    );
    return [];
  }
}

export async function getReGenerationMetricsSourceArea(
  startDate: string,
  endDate: string,
  energyTypes?: string[],
  areas?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add energy types if provided
    if (energyTypes && energyTypes.length > 0) {
      queryParams += `&energy_type=${energyTypes.join(",")}`;
    }

    // Add areas if provided
    if (areas && areas.length > 0) {
      queryParams += `&area=${areas.join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/re-generation-metrics-source-area?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        slot: item.slot,
        energy_type: item.energy_type,
        total_areas: item.total_areas,

        // Source-level metrics
        source_min_value: parseFloat(item.source_min_value?.toString() || "0"),
        source_max_value: parseFloat(item.source_max_value?.toString() || "0"),
        source_avg_value: parseFloat(item.source_avg_value?.toString() || "0"),
        source_mode_value: parseFloat(
          item.source_mode_value?.toString() || "0",
        ),

        // Area-level data
        areas:
          item.areas?.map((area: any) => ({
            area: area.area,
            slot: area.slot,
            energy_type: area.energy_type,

            // Area metrics
            avg_value: parseFloat(area.avg_value?.toString() || "0"),
            max_value: parseFloat(area.max_value?.toString() || "0"),
            min_value: parseFloat(area.min_value?.toString() || "0"),
            mode_value: parseFloat(area.mode_value?.toString() || "0"),
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching RE Generation Metrics Source/Area data:",
      error,
    );
    return [];
  }
}
export async function getReGenerationDailySourceArea(
  startDate: string,
  endDate: string,
  energyTypes?: string[],
  areas?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add energy types if provided
    if (energyTypes && energyTypes.length > 0) {
      queryParams += `&energy_type=${energyTypes.join(",")}`;
    }

    // Add areas if provided
    if (areas && areas.length > 0) {
      queryParams += `&area=${areas.join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/re-generation-daily-source-area?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        date: item.date,
        energy_type: item.energy_type,
        total_areas: item.total_areas,

        // Source-level daily metrics
        source_avg_daily_value_mw: parseFloat(
          item.source_avg_daily_value_mw?.toString() || "0",
        ),
        source_peak_daily_value_mw: parseFloat(
          item.source_peak_daily_value_mw?.toString() || "0",
        ),

        // Area-level data
        areas:
          item.areas?.map((area: any) => ({
            area: area.area,
            date: area.date,
            energy_type: area.energy_type,

            // Area daily metrics
            avg_daily_value_mw: parseFloat(
              area.avg_daily_value_mw?.toString() || "0",
            ),
            daily_peak_value_mw: parseFloat(
              area.daily_peak_value_mw?.toString() || "0",
            ),

            // Formatted time period data
            formatted_data:
              area.formatted_data?.map((period: any) => ({
                time_period: period.time_period,
                avg_value: parseFloat(period.avg_value?.toString() || "0"),
                max_value: parseFloat(period.max_value?.toString() || "0"),
                min_value: parseFloat(period.min_value?.toString() || "0"),
                max_value_slot: period.max_value_slot,
                min_value_slot: period.min_value_slot,
              })) || [],
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching RE Generation Daily Source/Area data:",
      error,
    );
    return [];
  }
}

export async function getReGenerationMonthlySourceArea(
  startDate: string,
  endDate: string,
  energyTypes?: string[],
  areas?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add energy types if provided
    if (energyTypes && energyTypes.length > 0) {
      queryParams += `&energy_type=${energyTypes.join(",")}`;
    }

    // Add areas if provided
    if (areas && areas.length > 0) {
      queryParams += `&area=${areas.join(",")}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/re-generation-monthly-source-area?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        year: item.year,
        month: item.month,
        energy_type: item.energy_type,
        total_areas: item.total_areas,

        // Source-level monthly metrics
        source_average_value_mw: parseFloat(
          item.source_average_value_mw?.toString() || "0",
        ),
        source_peak_value_mw: parseFloat(
          item.source_peak_value_mw?.toString() || "0",
        ),

        // Area-level data
        areas:
          item.areas?.map((area: any) => ({
            area: area.area,
            year: area.year,
            month: area.month,
            energy_type: area.energy_type,

            // Area monthly metrics
            peak_value_mw: parseFloat(area.peak_value_mw?.toString() || "0"),
            average_value_mw: parseFloat(
              area.average_value_mw?.toString() || "0",
            ),

            // Peak timing details
            date_of_peak_value: area.date_of_peak_value,
            slot_of_peak_value: area.slot_of_peak_value,

            // Evening peak data
            evening_peak_value_mw: parseFloat(
              area.evening_peak_value_mw?.toString() || "0",
            ),
            date_of_evening_peak_value: area.date_of_evening_peak_value,
            slot_of_evening_peak_value: area.slot_of_evening_peak_value,

            // Morning peak data
            morning_peak_value_mw: parseFloat(
              area.morning_peak_value_mw?.toString() || "0",
            ),
            date_of_morning_peak_value: area.date_of_morning_peak_value,
            slot_of_morning_peak_value: area.slot_of_morning_peak_value,
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching RE Generation Monthly Source/Area data:",
      error,
    );
    return [];
  }
}
export async function getIsgsMetricsSourceState(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  states?: string[],
  utilities?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add generation sources if provided
    if (generationSources && generationSources.length > 0) {
      const encodedSources = generationSources
        .map((s) => encodeURIComponent(s.toLowerCase()))
        .join(",");
      queryParams += `&generation_source=${encodedSources}`;
    }

    // Add states if provided
    if (states && states.length > 0) {
      const encodedStates = states.map((s) => encodeURIComponent(s)).join(",");
      queryParams += `&state=${encodedStates}`;
    }

    // Add utilities if provided
    if (utilities && utilities.length > 0) {
      const encodedUtilities = utilities
        .map((u) => encodeURIComponent(u))
        .join(",");
      queryParams += `&utility=${encodedUtilities}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/isgs-metrics-source-state-utility?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        slot: item.slot,
        generation_source: item.generation_source,
        total_states: item.total_states,
        total_utilities: parseFloat(item.total_utilities?.toString() || "0"),

        // Source-level metrics
        source_min_value: parseFloat(item.source_min_value?.toString() || "0"),
        source_max_value: parseFloat(item.source_max_value?.toString() || "0"),
        source_avg_value: parseFloat(item.source_avg_value?.toString() || "0"),
        source_mode_value: parseFloat(
          item.source_mode_value?.toString() || "0",
        ),

        // State-level data
        states:
          item.states?.map((state: any) => ({
            slot: state.slot,
            state_name: state.state_name,
            generation_source: state.generation_source,
            total_utilities: state.total_utilities,

            // State metrics
            state_min_value: parseFloat(
              state.state_min_value?.toString() || "0",
            ),
            state_max_value: parseFloat(
              state.state_max_value?.toString() || "0",
            ),
            state_avg_value: parseFloat(
              state.state_avg_value?.toString() || "0",
            ),
            state_mode_value: parseFloat(
              state.state_mode_value?.toString() || "0",
            ),

            // Utility-level data
            utilities:
              state.utilities?.map((utility: any) => ({
                slot: utility.slot,
                state_name: utility.state_name,
                utility_name: utility.utility_name,
                generation_source: utility.generation_source,

                // Utility metrics
                min_value: parseFloat(utility.min_value?.toString() || "0"),
                max_value: parseFloat(utility.max_value?.toString() || "0"),
                avg_value: parseFloat(utility.avg_value?.toString() || "0"),
                mode_value: parseFloat(utility.mode_value?.toString() || "0"),
              })) || [],
          })) || [],
      };
    });
  } catch (error) {
    console.error("Error fetching ISGS Metrics Source/State data:", error);
    return [];
  }
}

export async function getIsgsMetricsDailySourceState(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  states?: string[],
  utilities?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add generation sources if provided
    if (generationSources && generationSources.length > 0) {
      const encodedSources = generationSources
        .map((s) => encodeURIComponent(s.toLowerCase()))
        .join(",");
      queryParams += `&generation_source=${encodedSources}`;
    }

    // Add states if provided
    if (states && states.length > 0) {
      const encodedStates = states.map((s) => encodeURIComponent(s)).join(",");
      queryParams += `&state=${encodedStates}`;
    }

    // Add utilities if provided
    if (utilities && utilities.length > 0) {
      const encodedUtilities = utilities
        .map((u) => encodeURIComponent(u))
        .join(",");
      queryParams += `&utility=${encodedUtilities}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/isgs-daily-source-state-utility?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        date: item.date,
        generation_source: item.generation_source,
        total_states: item.total_states,
        total_utilities: parseFloat(item.total_utilities?.toString() || "0"),

        // Source-level daily metrics
        source_avg_daily_value_mw: parseFloat(
          item.source_avg_daily_value_mw?.toString() || "0",
        ),
        source_peak_daily_value_mw: parseFloat(
          item.source_peak_daily_value_mw?.toString() || "0",
        ),

        // State-level data
        states:
          item.states?.map((state: any) => ({
            date: state.date,
            state_name: state.state_name,
            total_utilities: state.total_utilities,
            generation_source: state.generation_source,

            // State daily metrics
            state_avg_daily_value_mw: parseFloat(
              state.state_avg_daily_value_mw?.toString() || "0",
            ),
            state_peak_daily_value_mw: parseFloat(
              state.state_peak_daily_value_mw?.toString() || "0",
            ),

            // Utility-level data
            utilities:
              state.utilities?.map((utility: any) => ({
                date: utility.date,
                state_name: utility.state_name,
                utility_name: utility.utility_name,
                generation_source: utility.generation_source,

                // Utility daily metrics
                avg_daily_value_mw: parseFloat(
                  utility.avg_daily_value_mw?.toString() || "0",
                ),
                daily_peak_value_mw: parseFloat(
                  utility.daily_peak_value_mw?.toString() || "0",
                ),

                // Formatted data (Morning/Evening breakdown)
                formatted_data:
                  utility.formatted_data?.map((period: any) => ({
                    time_period: period.time_period,
                    avg_value: parseFloat(period.avg_value?.toString() || "0"),
                    max_value: parseFloat(period.max_value?.toString() || "0"),
                    min_value: parseFloat(period.min_value?.toString() || "0"),
                    max_value_slot: period.max_value_slot,
                    min_value_slot: period.min_value_slot,
                  })) || [],
              })) || [],
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching ISGS Daily Metrics Source/State data:",
      error,
    );
    return [];
  }
}
export async function getIsgsMetricsMonthlySourceState(
  startDate: string,
  endDate: string,
  generationSources?: string[],
  states?: string[],
  utilities?: string[],
): Promise<any[]> {
  try {
    // Build query parameters
    let queryParams = `start_date=${startDate}&end_date=${endDate}`;

    // Add generation sources if provided
    if (generationSources && generationSources.length > 0) {
      const encodedSources = generationSources
        .map((s) => encodeURIComponent(s.toLowerCase()))
        .join(",");
      queryParams += `&generation_source=${encodedSources}`;
    }

    // Add states if provided
    if (states && states.length > 0) {
      const encodedStates = states.map((s) => encodeURIComponent(s)).join(",");
      queryParams += `&state=${encodedStates}`;
    }

    // Add utilities if provided
    if (utilities && utilities.length > 0) {
      const encodedUtilities = utilities
        .map((u) => encodeURIComponent(u))
        .join(",");
      queryParams += `&utility=${encodedUtilities}`;
    }

    const res = await buildHttpResponse({
      endPoint: `rajasthan/isgs-monthly-source-state-utility?${queryParams}`,
      method: HttpMethod.GET,
      noCache: true,
    });

    const data = Array.isArray(res) ? res : res?.data || [];

    if (!Array.isArray(data)) {
      console.error("API did not return an array of data:", res);
      return [];
    }

    return data.map((item: any) => {
      return {
        year: item.year,
        month: item.month,
        generation_source: item.generation_source,
        total_states: item.total_states,
        total_utilities: parseFloat(item.total_utilities?.toString() || "0"),

        // Source-level monthly metrics
        source_average_value_mw: parseFloat(
          item.source_average_value_mw?.toString() || "0",
        ),
        source_peak_value_mw: parseFloat(
          item.source_peak_value_mw?.toString() || "0",
        ),

        // State-level data
        states:
          item.states?.map((state: any) => ({
            year: state.year,
            month: state.month,
            state_name: state.state_name,
            total_utilities: state.total_utilities,
            generation_source: state.generation_source,

            // State monthly metrics
            state_average_value_mw: parseFloat(
              state.state_average_value_mw?.toString() || "0",
            ),
            state_peak_value_mw: parseFloat(
              state.state_peak_value_mw?.toString() || "0",
            ),

            // Utility-level data
            utilities:
              state.utilities?.map((utility: any) => ({
                year: utility.year,
                month: utility.month,
                state_name: utility.state_name,
                utility_name: utility.utility_name,
                generation_source: utility.generation_source,

                // Utility monthly metrics
                peak_value_mw: parseFloat(
                  utility.peak_value_mw?.toString() || "0",
                ),
                average_value_mw: parseFloat(
                  utility.average_value_mw?.toString() || "0",
                ),

                // Peak timing details
                date_of_peak_value: utility.date_of_peak_value,
                slot_of_peak_value: utility.slot_of_peak_value,

                // Evening peak data
                evening_peak_value_mw: parseFloat(
                  utility.evening_peak_value_mw?.toString() || "0",
                ),
                date_of_evening_peak_value: utility.date_of_evening_peak_value,
                slot_of_evening_peak_value: utility.slot_of_evening_peak_value,

                // Morning peak data
                morning_peak_value_mw: parseFloat(
                  utility.morning_peak_value_mw?.toString() || "0",
                ),
                date_of_morning_peak_value: utility.date_of_morning_peak_value,
                slot_of_morning_peak_value: utility.slot_of_morning_peak_value,
              })) || [],
          })) || [],
      };
    });
  } catch (error) {
    console.error(
      "Error fetching ISGS Monthly Metrics Source/State data:",
      error,
    );
    return [];
  }
}

export interface DPROtherItem {
  date: string;
  slot: number;
  total_injection: number;
  cs_share_plus_sail_bsp_plus_urs_ex_csptcl: number;
  interstate_stoa_ex_csptcl: number;
  total_availability: number;
  bilateral_imp_or_exp_cspdcl_ex_csptcl_plus_telangana_plus_lta: number;
  inhand_availability: number;
  cs_schedule: number;
  drawal_as_per_scada: number;
  demand: number;
  od_ud: number;
  unscheduled_load_shedding: number;
  frequency_correction: number;
  total_unrestricted_demand: number;
  cs_share_surrendered_mw: number;
  urs_power: number;
  reserve_capacity_available: number;
  voilation_of_dsm_12_percent_volume: string;
}

export async function getDPROther(
  startDate: string,
  endDate: string,
): Promise<DPROtherItem[]> {
  try {
    const response = await buildHttpResponse({
      endPoint: `chhattisgarh/getDPROther?start_date=${startDate}&end_date=${endDate}`,
      method: HttpMethod.GET,
      noCache: true,
    });
    return toArray<DPROtherItem>(response);
  } catch (error) {
    console.error("Error fetching Chhattisgarh DPR Other:", error);
    return [];
  }
}
export async function getDiscomLoadMetrics({
  ranges,
  aggregated,
}: any): Promise<any[]> {
  // Build query string from parameters.
  let query = `?aggregated=${aggregated}`;

  // Add ranges as JSON array
  const rangesJson = JSON.stringify(ranges);
  query += `&ranges=${encodeURIComponent(rangesJson)}`;

  // Adjust the endpoint URL as needed.
  const response = await buildHttpResponse({
    endPoint: `chhattisgarh/getChhattisgarhLoadMetrics${query}`,
    method: HttpMethod.GET,
  });

  return response;
}
export async function getTrendMetrics({
  ranges,
  aggregated,
  selectedTypes,
}: any): Promise<any[]> {
  let query = `?aggregated=${aggregated}`;

  // Encode ranges as a JSON array in the query string
  query += `&ranges=${encodeURIComponent(JSON.stringify(ranges))}`;

  // Append type only when provided — avoids "&type=undefined" in the URL
  if (selectedTypes && selectedTypes.length > 0) {
    // Send as a comma-separated list so the backend receives e.g. "demand,demand_forecast_d1"
    query += `&type=${encodeURIComponent(selectedTypes.join(","))}`;
  }

  const response = await buildHttpResponse({
    endPoint: `chhattisgarh/getChhattisgarhTrendMetrics${query}`,
    method: HttpMethod.GET,
  });

  return response;
}

export async function ModAggregatedLoadData(
  ModAggregatedData: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postAggregatedLoadPrice`,
    method: HttpMethod.POST,
    body: ModAggregatedData,
  });

  return response as ApiResponse;
}

export async function ModDamData(ModDamdata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postDam`,
    method: HttpMethod.POST,
    body: ModDamdata,
  });

  return response as ApiResponse;
}
export async function ModDemandData(ModDemanddata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postDemand`,
    method: HttpMethod.POST,
    body: ModDemanddata,
  });

  return response as ApiResponse;
}

export async function CreateBidTable(ModUpdatedata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/createBid`,
    method: HttpMethod.POST,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}

export async function fetchModTableData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getMOD?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModUpdateData(ModUpdatedata: any): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postMOD`,
    method: HttpMethod.POST,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}

export async function fetchModSimulationSummary(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getSimulatorGenChart?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function fetchModSimulationStation(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getData4?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModSimulationTopBuyer(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getSimulatorGenPrice?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModSimulationSaleRevenue(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getSimulatorSalesRevenue?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModSimulationCostPower(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getSimulatorCostPower?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function getModGridcoBidSimulationData(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getSimulatorBid?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModStagePost(stage: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postStage`,
    method: HttpMethod.POST,
    body: {
      stage: stage,
      session: session,
    },
  });
  return response;
}

export async function fetchLgbrTableData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/get-lgbr?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchLgbrGetData(
  type: string | null,
  time_slot: number | null,
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/get-lgbr-generator?date=${startdate}&session=${session}&type=${type}&time_slot=${time_slot}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function fetchModGridcoArchive(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getPreviousSession?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModGridcoActivateSession(session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/activateSession`,
    method: HttpMethod.POST,
    body: {
      session: session,
    },
    noCache: true,
  });
  return response;
}
export async function postMODChartClassified(
  payload: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postMODChartClassified`,
    method: HttpMethod.POST,
    body: payload,
  });

  return response as ApiResponse;
}
export async function fetchModTableChartDataClassified(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getMODChartClassified?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
// Classes grouped chart data
export async function fetchModChartClassGrouped(
  startdate: string,
  session: number,
) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getMODChartClassGrouped?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function postModChartClassGrouped(
  payload: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postMODChartClassGrouped`,
    method: HttpMethod.POST,
    body: payload,
  });
  return response as ApiResponse;
}

// Class Bid - create (POST) and get (GET)
export async function createClassBid(): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/createClassBid`,
    method: HttpMethod.POST,
    body: {},
  });
  return response as ApiResponse;
}

export async function getClassBid() {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getClassBid`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}

export async function postClassBid(payload: {
  data: Array<{
    time_slot__slot: number;
    generator_class: number;
    bid_category?: string;
    price?: number;
    class_rate?: number;
  }>;
}): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postClassBid`,
    method: HttpMethod.POST,
    body: payload,
  });
  return response as ApiResponse;
}

export async function fetchModBidTableData(startdate: string, session: number) {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/getBid?date=${startdate}&session=${session}`,
    method: HttpMethod.GET,
    noCache: true,
  });
  return response;
}
export async function ModUpdateBidData(
  ModUpdatedata: any,
): Promise<ApiResponse> {
  const response = await buildHttpResponse({
    endPoint: `mod_cspdcl/postBid`,
    method: HttpMethod.POST,
    body: ModUpdatedata,
  });

  return response as ApiResponse;
}